import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiCheck, FiLoader } from 'react-icons/fi';
import { useParams } from 'react-router-dom';
import Toast from '../../components/Toast';
import AIInterviewSetup from './AIInterviewSetup';
import AIInterviewCompletion from './AIInterviewCompletion';
import AIInterviewHistory from './AIInterviewHistory';
import MeetingRoom from '../meeting/MeetingRoom';
import PreflightGate from '../meeting/PreflightGate';
import ConfirmDialog from '../meeting/components/ConfirmDialog';
import TaskPrompt from './components/TaskPrompt';
import { useMedia } from '../interview/context/MediaContext';
import { speak, stopSpeaking, createRecognizer, sttSupported } from './hooks/useSpeech';
import { decideVoiceTurn, describeVoiceFailure, pcmFrameHasVoice, shouldFinalizeSparseNoCaption,
  voiceSilenceRemaining, VOICE_REPEAT_PROMPT, MAX_VOICE_CLARIFICATIONS, withVoiceDeadline } from './utils/voiceTurnDecision';
import { appendTypedAttempt, appendVoiceAttempt, updateLastCandidate } from './utils/candidateMessages';
import { spokenText } from './utils/spokenText';
import {
  startInterview,
  sendTurn,
  transcribeVoiceTurn,
  uploadVoiceChunk,
  getVoiceUploadStatus,
  finalizeVoiceUpload,
  endInterview,
  getStatus,
  getResult,
  getTranscript,
  getHistory,
  getHistoryDetail,
  sendPresence,
  presenceBeacon,
  startMockInterview,
  sendMockTurn,
  transcribeMockVoiceTurn,
  endMockInterview,
  getMockStatus,
  getMockResult,
  getMockTranscript,
  getMockHistory,
  getMockHistoryDetail,
  getMockAttempts,
  sendMockPresence,
  mockPresenceBeacon,
  submitTaskAnswer,
} from './utils/aiInterviewApi';
import { VoiceUploadManager } from './utils/voiceUploadManager';
import { preparationStepState, preparationSubstageLabel } from './utils/preparationProgress';

/**
 * Hybrid voice interview.
 *   • Browser Web Speech API → live captions + endpointing/turn-taking (robust noise handling).
 *   • Per-turn mic PCM → configured server STT (/media/transcribe) → verified transcript → engine.
 *   • Engine reply (text) → browser SpeechSynthesis speaks it (no server TTS/streaming).
 * The interview engine is untouched — reached only through /start, /turn, /media/transcribe.
 */
const PCM_VAD_MIN_SPEECH_MS = 220;
const PCM_VAD_SILENCE_MS = 5000;    // submit only after this much silence following speech
const PCM_VAD_NO_SPEECH_RESTART_MS = 18000; // restart dead listeners, but never submit because of duration
const PCM_VAD_MIN_AUDIO_MS = 250;
const PCM_VAD_BURST_RESET_MS = 200;

// Skip is intentionally not surfaced in the meeting UI (the engine rephrases instead of
// advancing). The capability still exists via sendTextTurn('skip') if a button is re-added.

// Default proctoring requirements when no admin config is passed (standalone use).
// *_required = must be on to JOIN · *_strict = must stay on for the whole interview.
const REQ_DEFAULTS = {
  cameraRequired: true, cameraStrict: false,
  screenRequired: true, screenStrict: false,
  micRequired: true,
};

// How often the page asks the server whether the interview has ended on its own (the server enforces the
// time budget + inactivity even when this tab is idle/backgrounded — see Fix 1). Surfaces that end here.
const POLL_MS = 15000;
const PREPARATION_POLL_MS = 2000;
const PREPARATION_ACTIVE = new Set(['queued', 'running', 'retrying']);

const preparationActive = (value) => PREPARATION_ACTIVE.has(String(value || '').toLowerCase());
const PREPARATION_STEPS = [
  { stage: 'role_setup', label: 'Reviewing the role and interview setup' },
  { stage: 'candidate_intelligence', label: 'Personalizing with your CV' },
  { stage: 'evidence_matrix', label: 'Matching role requirements with available evidence' },
  { stage: 'objective_generation', label: 'Prioritizing interview topics' },
  { stage: 'question_plan_generation', label: 'Preparing your questions' },
];
// Default check-in interval / count if the server hasn't sent its inactivity config yet (it overrides these
// via the turn response: nudge_after_seconds, nudge_count). Wiza checks in this often while it's the
// candidate's turn and they stay silent, then ends after the configured number of check-ins.
const INACTIVITY_MS = 120000;
const NUDGE_COUNT_DEFAULT = 3;
const NUDGES = [
  "Hey, are you still with me? Take your time — whenever you're ready, just go ahead.",
  "No rush at all — I'm still here whenever you'd like to continue. Is everything okay on your end?",
  "Just checking in — if you're having any trouble with your mic or connection, let me know. I'm here whenever you're ready.",
];

const fmtTaskTime = (secs) => {
  const s = Math.max(0, Math.floor(Number(secs || 0)));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const PracticalTaskModal = ({
  task,
  answer,
  onAnswer,
  onPaste,
  onSubmit,
  submitting,
}) => {
  if (!task) return null;
  const signals = Array.isArray(task.evaluation_signals) ? task.evaluation_signals : [];
  const remaining = Number(task.remaining_seconds ?? task.duration_seconds ?? 0);
  const nearEnd = remaining > 0 && remaining <= 60;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
      <div className="flex max-h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/10 dark:bg-slate-950 dark:ring-white/10">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 px-6 py-5 dark:border-slate-800">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wide text-primary-600 dark:text-primary-300">Practical task</p>
            <h2 className="mt-1 text-xl font-bold leading-tight text-slate-950 dark:text-white">
              {task.title || 'Role-specific practical task'}
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Complete this task in the window. The interview will continue after submission.
            </p>
          </div>
          <div className={`rounded-2xl px-4 py-2 text-center ring-1 ${nearEnd ? 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-950/50 dark:text-rose-200 dark:ring-rose-800' : 'bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-950/50 dark:text-indigo-200 dark:ring-indigo-800'}`}>
            <p className="text-[10px] font-bold uppercase tracking-wide">Time left</p>
            <p className="mt-0.5 text-2xl font-black tabular-nums">{fmtTaskTime(remaining)}</p>
          </div>
        </div>

        <div className="grid min-h-0 flex-1 gap-0 overflow-hidden lg:grid-cols-[0.95fr_1.45fr]">
          <div className="min-h-0 overflow-y-auto border-b border-slate-200 p-6 dark:border-slate-800 lg:border-b-0 lg:border-r">
            <h3 className="text-sm font-bold text-slate-900 dark:text-white">Task details</h3>
            <TaskPrompt
              text={task.candidate_prompt}
              className="mt-3 rounded-xl bg-slate-50 p-4 text-sm leading-6 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-800"
            />

            {signals.length > 0 && (
              <div className="mt-5 space-y-4">
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">How this will be assessed</h4>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-700 dark:text-slate-200">
                    {signals.slice(0, 5).map((item, idx) => <li key={`signal-${idx}`}>{item}</li>)}
                  </ul>
                </div>
              </div>
            )}
          </div>

          <div className="flex min-h-0 flex-col p-6">
            <div className="mb-3 flex items-center justify-between gap-3">
              <label className="text-sm font-bold text-slate-900 dark:text-white" htmlFor="practical-task-answer">
                Your answer
              </label>
              {nearEnd && <span className="rounded-full bg-rose-50 px-3 py-1 text-xs font-bold text-rose-700 dark:bg-rose-950/50 dark:text-rose-200">Less than 1 minute left</span>}
            </div>
            <textarea
              id="practical-task-answer"
              value={answer}
              onChange={(e) => onAnswer(e.target.value)}
              onPaste={onPaste}
              disabled={submitting}
              autoFocus
              className="min-h-[360px] flex-1 resize-none rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm leading-6 text-slate-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200 disabled:opacity-60 dark:border-slate-800 dark:bg-slate-900 dark:text-white dark:focus:border-primary-500 dark:focus:ring-primary-900"
              placeholder="Write your structured answer here. You can submit before the timer ends."
            />
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">
                If time expires, your current answer will be submitted automatically.
              </p>
              <button
                type="button"
                onClick={onSubmit}
                disabled={submitting}
                className="rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-primary-600/20 transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? 'Submitting...' : 'Submit task'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const VoiceInterviewHybridPage = ({
  embedded = false,                 // rendered inside the onboarding experience (streams already live)
  sessionId: sessionIdProp,
  candidateId: candidateIdProp,
  interviewCfg,                     // admin config's `interview` section (camera/screen/mic rules)
  initialAudioWarning = false,
  onSessionComplete,
  rejoin = false,                   // resuming an unfinished interview → restore prior context
  mock,                             // Job Seeker Mock Interview: {uid, jobListingId} → use the mock API
} = {}) => {
  const params = useParams();
  // Job Seeker Mock Interview: same engine/UI, keyed by (uid + job_listing_id), voice+chat only — no
  // camera / screen / identity / recording (the mic/voice check still applies).
  const isMock = !!(mock && mock.uid != null && mock.jobListingId);
  // Resolved requirements: mock forces camera/screen OFF + mic ON; else admin config wins, else defaults.
  const RC = isMock
    ? { cameraRequired: false, cameraStrict: false, screenRequired: false, screenStrict: false, micRequired: true }
    : {
        cameraRequired: interviewCfg?.camera_required ?? REQ_DEFAULTS.cameraRequired,
        cameraStrict: interviewCfg?.camera_strict ?? REQ_DEFAULTS.cameraStrict,
        screenRequired: interviewCfg?.screen_required ?? REQ_DEFAULTS.screenRequired,
        screenStrict: interviewCfg?.screen_strict ?? REQ_DEFAULTS.screenStrict,
        micRequired: interviewCfg?.mic_required ?? REQ_DEFAULTS.micRequired,
      };
  const [phase, setPhase] = useState('setup');           // setup | preflight | call | done | history
  useEffect(() => {
    if (phase === 'done') onSessionComplete?.();
  }, [phase, onSessionComplete]);
  const [sessionId, setSessionId] = useState(sessionIdProp || params.sessionId || '');
  const [candidateId, setCandidateId] = useState(candidateIdProp || params.candidateId || '');
  const [toast, setToast] = useState({ message: '', type: 'error' });

  // ── API adapter: one uniform interface over the HR endpoints (session_id + candidate_id) OR the Job
  // Seeker Mock endpoints (uid + job_listing_id). A ref so effects don't churn but always see latest ids.
  const apiRef = useRef(null);
  {
    const SID = () => sessionId.trim();
    const CID = () => candidateId.trim();
    apiRef.current = isMock
      ? {
          start: () => startMockInterview(mock.uid, mock.jobListingId),
          turn: (m, pasted) => sendMockTurn(mock.uid, mock.jobListingId, m, pasted),
          transcribe: (pcm, fb) => transcribeMockVoiceTurn(mock.uid, mock.jobListingId, pcm, fb),
          end: (reason) => endMockInterview(mock.uid, mock.jobListingId, reason),
          status: () => getMockStatus(mock.uid, mock.jobListingId),
          result: (iid, refresh, basis) => getMockResult(mock.uid, mock.jobListingId, iid, refresh, basis),
          transcript: () => getMockTranscript(mock.uid, mock.jobListingId),
          history: () => getMockHistory(mock.uid, mock.jobListingId),
          historyDetail: () => getMockHistoryDetail(mock.uid, mock.jobListingId),
          attempts: () => getMockAttempts(mock.uid, mock.jobListingId),
          taskAnswer: async () => { throw new Error('Practical tasks are not available in mock interviews yet.'); },
          presence: (present, rejoined, invitationId) => sendMockPresence(mock.uid, mock.jobListingId, present, rejoined, invitationId),
          presenceBeacon: (present) => mockPresenceBeacon(mock.uid, mock.jobListingId, present),
        }
      : {
          start: () => startInterview(SID(), CID()),
          turn: (m, pasted, seq) => sendTurn(SID(), CID(), m, pasted, seq),
          transcribe: (pcm, fb, seq, source, diagnostics) => transcribeVoiceTurn(SID(), CID(), pcm, fb, 'en', capture.lowQualityAudio, seq, source, diagnostics),
          uploadChunk: (id, seq, pcm) => uploadVoiceChunk(SID(), CID(), id, seq, pcm),
          uploadStatus: (id) => getVoiceUploadStatus(SID(), CID(), id),
          finalizeUpload: (id, details) => finalizeVoiceUpload(SID(), CID(), id, { ...details, low_quality: capture.lowQualityAudio }),
          end: (reason) => endInterview(SID(), CID(), reason),
          status: () => getStatus(SID(), CID()),
          result: (iid, refresh, basis) => getResult(SID(), CID(), iid, refresh, basis),
          transcript: () => getTranscript(SID(), CID()),
          history: () => getHistory(SID(), CID()),
          historyDetail: () => getHistoryDetail(SID(), CID()),
          attempts: async () => ({ attempts: [] }),   // HR path has no per-job attempts strip
          taskAnswer: (taskId, answer, clientTaskId, status, spent, integrityFlags) => (
            submitTaskAnswer(SID(), CID(), taskId, answer, clientTaskId, status, spent, integrityFlags)
          ),
          presence: (present, rejoined, invitationId) => sendPresence(SID(), CID(), present, rejoined, invitationId),
          presenceBeacon: (present, options) => presenceBeacon(SID(), CID(), present, options),
        };
  }

  // call state
  const [messages, setMessages] = useState([]);          // {role, text}
  const [, setCaption] = useState('');                   // provisional text, never rendered while listening
  const [inputActive, setInputActive] = useState(false);
  const [aiState, setAiState] = useState('idle');        // speaking | thinking | listening | idle
  const [voiceRecovery, setVoiceRecovery] = useState('none');
  const [isRejoining, setIsRejoining] = useState(rejoin);
  const [muted, setMuted] = useState(rejoin);
  const [micStarting, setMicStarting] = useState(false);
  const [mutedSpeechVisible, setMutedSpeechVisible] = useState(false);
  const [audioIssueKind, setAudioIssueKind] = useState(initialAudioWarning ? 'device' : '');
  const [textMode, setTextMode] = useState(!sttSupported);
  const [typed, setTyped] = useState('');
  const [stage, setStage] = useState('');
  const [topicProgress, setTopicProgress] = useState(null);
  const [roleTitle, setRoleTitle] = useState('');
  const [elapsed, setElapsed] = useState(null);
  const [progress, setProgress] = useState({ asked: 0, target: null, total: null });
  const [closingSeconds, setClosingSeconds] = useState(null);
  const [winding, setWinding] = useState(false);         // interview is wrapping up/closing → stop proctoring nags
  const [activeTask, setActiveTask] = useState(null);
  const [taskAnswer, setTaskAnswer] = useState('');
  const [taskConfirmOpen, setTaskConfirmOpen] = useState(false);
  const [taskSubmitting, setTaskSubmitting] = useState(false);
  const [taskIntegrity, setTaskIntegrity] = useState({ paste_count: 0, pasted_characters: 0, large_insert_count: 0 });
  const [preparation, setPreparation] = useState({
    status: '', stage: '', attempt: 0, retryCount: 0, degradedReasons: [], error: '', hasResume: null, stageUpdatedAt: '', questionProgress: null,
  });
  const [preparationNow, setPreparationNow] = useState(Date.now());
  useEffect(() => {
    if (!preparationActive(preparation.status)) return undefined;
    const timer = setInterval(() => setPreparationNow(Date.now()), 5000);
    return () => clearInterval(timer);
  }, [preparation.status]);

  // meeting media (camera required + entire-screen share for proctoring)
  const [hideMirror, setHideMirror] = useState(true);    // hide own shared screen to avoid mirror loop
  const [shareWarning, setShareWarning] = useState('');
  const [audioWarning, setAudioWarning] = useState(initialAudioWarning
    ? 'We could not hear you clearly in the microphone test. Check your device or browser microphone settings, then retry. You can also type your answer.' : '');
  const [audioWarningVisible, setAudioWarningVisible] = useState(!!initialAudioWarning);
  const [audioWarningVersion, setAudioWarningVersion] = useState(0);
  useEffect(() => {
    if (!audioWarning || !audioWarningVisible || ['repeat', 'manual', 'checking'].includes(voiceRecovery)) return undefined;
    const timer = setTimeout(() => setAudioWarningVisible(false), 10000);
    return () => clearTimeout(timer);
  }, [audioWarning, audioWarningVisible, audioWarningVersion, voiceRecovery]);
  const [joining, setJoining] = useState(false);
  const [startError, setStartError] = useState('');   // embedded entry failed → show retry, not a spinner
  const [confirmEnd, setConfirmEnd] = useState(false); // themed "end interview?" modal
  const [endRemaining, setEndRemaining] = useState(null); // seconds left when the End modal opened (rejoin gate)

  // result state (reuses completion screen)
  const [transcript, setTranscript] = useState('');
  const [meta, setMeta] = useState({});
  const [evaluation, setEvaluation] = useState(null);
  const [evaluating, setEvaluating] = useState(false);
  const [resultId, setResultId] = useState(null);
  const [history, setHistory] = useState([]);
  // Job-seeker mock feedback (candidate-facing skill acknowledgement + cross-attempt history).
  const [acknowledgement, setAcknowledgement] = useState(null);
  const [skillScores, setSkillScores] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Camera / mic / screen are owned by MediaProvider so they persist across onboarding → interview.
  const { camera, screen, capture, startMic, restartMic, setFrameSink, stopAll, speakAloud, stopAiVoice } = useMedia();

  // Speak an AI line with the SERVER voice (ElevenLabs/Piper) — heard live AND captured in the
  // recording from one fetch. Resolves when she finishes (callers await it before listening). `onStart`
  // (when given) fires with the clip duration so the caption can reveal in sync. If the server TTS is
  // unavailable, fall back to the browser's built-in SpeechSynthesis so she still talks.
  const speakAi = async (text, opts = {}) => {
    text = spokenText(text);
    if (!text) return;
    speakingAudioRef.current = true;   // audio is now actively playing → single-turn guards hold
    try {
      try {
        if (speakAloud && await speakAloud(text, opts)) return;
      } catch { /* fall through to the browser voice */ }
      if (opts.onStart) {
        const wc = (text || '').trim().split(/\s+/).filter(Boolean).length;   // ~155 wpm estimate
        try { opts.onStart(Math.max(0.7, wc / 2.6)); } catch { /* ignore */ }
      }
      await speak(text);
    } finally {
      speakingAudioRef.current = false;  // audio finished (incl. early return / error) → re-arm may proceed
      lastAiAudioEndRef.current = Date.now();
      const invitation = finalQaRef.current;
      if (invitation?.awaiting_reply && invitation.invitation_id && !acknowledgedQaRef.current.has(invitation.invitation_id)) {
        acknowledgedQaRef.current.add(invitation.invitation_id);
        apiRef.current.presence(true, false, invitation.invitation_id).catch(() => {});
      }
    }
  };
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const endedRef = useRef(false);
  const concludingRef = useRef(false);      // speaking the closing message + holding (guards re-entry)
  const windingRef = useRef(false);         // synchronous mirror of `winding` for the imperative guards
  windingRef.current = winding;
  const recResumeRef = useRef(0);           // bounded resume count to keep a recognizer error-loop in check
  const pendingMicRestartRef = useRef(false);
  const micRecoveringRef = useRef(false);
  const busyRef = useRef(false);
  const closeDelayRef = useRef(10);
  const pastedRef = useRef(false);          // a paste happened while composing the current typed answer
  const lastTypedRef = useRef('');          // last value of the text box (to spot a big paste jump)
  const rejoinMinRef = useRef(60);          // min seconds left for the End dialog to offer "keep going" (from server)
  const lastTouchRef = useRef(0);           // last activity heartbeat sent to the backend (throttle)

  // recording (per-turn PCM) + recognizer plumbing
  const recChunksRef = useRef([]);          // Int16 PCM ArrayBuffers captured during the current turn
  const uploadRef = useRef(null);
  const uploadRejectedRef = useRef(false);
  const uploadCanTypeRef = useRef(false);
  const recoveryStateRef = useRef('none');
  const recoveryHandlingRef = useRef(false);
  const recoveryManualRef = useRef(false);
  const questionKeyRef = useRef('');
  const clarificationCountRef = useRef(0);
  const lastCaptionActivityRef = useRef(0);
  const lastCaptionResultRef = useRef('');
  const deliveryCheckRef = useRef(null);
  const recordedBytesRef = useRef(0);
  const recordingRef = useRef(false);       // append frames only while truly listening
  const vadSpeechMsRef = useRef(0);         // speech duration observed directly from PCM frames
  const vadActiveMsRef = useRef(0);
  const vadLastSpeechAtRef = useRef(0);
  const vadCandidateAtRef = useRef(0);
  const vadBurstMsRef = useRef(0);
  const vadDebugAtRef = useRef(0);
  const vadFinalizeRef = useRef(null);
  const vadMaxRef = useRef(null);
  const recognizerRef = useRef(null);
  const silenceRef = useRef(null);
  const inactivityRef = useRef(null);       // "are you there?" check-in timer
  const nudgeCountRef = useRef(0);          // how many check-ins sent this turn
  const nudgeIntervalRef = useRef(INACTIVITY_MS);   // silence interval before/between check-ins (from server)
  const nudgeMaxRef = useRef(NUDGE_COUNT_DEFAULT);  // how many check-ins before ending (from server)
  const captionRef = useRef('');
  const finalCaptionRef = useRef('');
  const interimCaptionRef = useRef('');
  const recognitionDiagnosticsRef = useRef({});
  const recognitionEpochRef = useRef(0);
  const captionSourceRef = useRef('unknown');
  const inputActiveRef = useRef(false);
  const listeningRef = useRef(false);
  const textModeRef = useRef(!sttSupported);
  textModeRef.current = textMode;
  const mutedRef = useRef(rejoin);            // synchronous mute state for the listening guard
  mutedRef.current = muted;
  const aiStateRef = useRef('idle');         // synchronous AI-state for the single-turn guards (no stale closures)
  aiStateRef.current = aiState;
  // TRUE only while Wiza's audio is ACTUALLY playing. `aiState==='speaking'` lingers after the audio ends
  // (it's cleared only by the NEXT state-setter), which made the single-turn guards below block the
  // legitimate post-speech re-arm forever → stuck "Speaking", mic never armed, and armInactivity() never
  // reached so the silence check-ins never fired. This ref is set/cleared around the real audio await in
  // speakAi, so the guards release the instant speech ends while still blocking true barge-in mid-speech.
  const speakingAudioRef = useRef(false);
  const lastAiAudioEndRef = useRef(0);
  const pageLifetimeRef = useRef(0);
  const joinAttemptRef = useRef(null);
  const micActivationRef = useRef(null);
  const restoredUploadRef = useRef(null);
  const audioIssueRef = useRef(initialAudioWarning ? 'device' : '');
  const mutedSpeechRef = useRef({ samples: [], nextAllowed: 0, timer: null });
  const captureSignalRef = useRef(null);
  const turnSeqRef = useRef(0);              // monotonic per-submit id → server coalesces a racing duplicate
  const activeTaskRef = useRef(null);
  const finalQaRef = useRef(null);
  const acknowledgedQaRef = useRef(new Set());
  const taskAnswerRef = useRef('');
  const taskSubmittingRef = useRef(false);
  const taskIntegrityRef = useRef(taskIntegrity);
  const preparationRef = useRef(preparation);
  const preparationGreetingHandledRef = useRef(false);
  activeTaskRef.current = activeTask;
  taskAnswerRef.current = taskAnswer;
  taskSubmittingRef.current = taskSubmitting;
  taskIntegrityRef.current = taskIntegrity;
  preparationRef.current = preparation;

  const showError = (m) => setToast({ message: m, type: 'error' });
  const showSuccess = (m) => setToast({ message: m, type: 'success' });
  function setRecoveryState(next) {
    recoveryStateRef.current = next;
    setVoiceRecovery(next);
  }
  function settleVoiceOperation(next = 'none') {
    busyRef.current = false;
    setRecoveryState(next);
    setAiState(next === 'checking' ? 'checking' : 'idle');
  }
  function beginVoiceOperation() {
    busyRef.current = true;
    setRecoveryState('processing');
    setAiState('thinking');
  }
  function updateQuestionContext(resp) {
    const key = (resp?.interviewer_message || '').trim();
    if (key && key !== questionKeyRef.current) {
      questionKeyRef.current = key;
      clarificationCountRef.current = 0;
      recoveryManualRef.current = false;
    }
  }
  const warnAudio = (message, kind = 'delivery') => {
    audioIssueRef.current = kind;
    setAudioIssueKind(kind);
    setAudioWarning(message);
    setAudioWarningVisible(true);
    setAudioWarningVersion((version) => version + 1);
  };
  const clearAudioWarning = () => {
    audioIssueRef.current = '';
    setAudioIssueKind('');
    setAudioWarning('');
    setAudioWarningVisible(false);
  };
  function dismissMutedSpeech() {
    const reminder = mutedSpeechRef.current;
    if (reminder.timer) clearTimeout(reminder.timer);
    reminder.timer = null;
    reminder.samples = [];
    setMutedSpeechVisible(false);
  }
  function applyMicMuted(value) {
    mutedRef.current = value;
    capture.setMuted(value);
    setMuted(value);
    dismissMutedSpeech();
  }
  function handleCaptureSignal(signal) {
    if (endedRef.current || phaseRef.current !== 'call') return;
    setMicStarting(false);
    if (audioIssueRef.current === 'device') clearAudioWarning();
    const reminder = mutedSpeechRef.current;
    const now = signal.at;
    if (!mutedRef.current || speakingAudioRef.current || now - lastAiAudioEndRef.current < 600
        || busyRef.current || activeTaskRef.current || windingRef.current || document.hidden
        || recoveryStateRef.current === 'checking' || textModeRef.current) {
      if (reminder.samples.length || reminder.timer) dismissMutedSpeech();
      return;
    }
    reminder.samples = reminder.samples.filter((sample) => now - sample.at < 1000);
    if (signal.speechLike) reminder.samples.push({ at: now, ms: Math.min(100, signal.durationMs) });
    if (now < reminder.nextAllowed || reminder.samples.reduce((sum, sample) => sum + sample.ms, 0) < 600) return;
    reminder.nextAllowed = now + 10000;
    reminder.samples = [];
    setMutedSpeechVisible(true);
    reminder.timer = setTimeout(() => {
      reminder.timer = null;
      setMutedSpeechVisible(false);
    }, 3000);
  }
  captureSignalRef.current = handleCaptureSignal;
  useEffect(() => {
    capture.setSignalSink?.((signal) => captureSignalRef.current?.(signal));
    return () => {
      capture.setSignalSink?.(null);
      clearTimeout(mutedSpeechRef.current.timer);
    };
  }, [capture.setSignalSink]);
  const addMsg = (role, text, deliveryStatus = null) => {
    if (text && text.trim()) setMessages((prev) => [...prev, { role, text, ts: new Date().toISOString(), deliveryStatus }]);
  };
  const addVoiceAttempt = (text) => {
    setMessages((prev) => appendVoiceAttempt(prev, text, new Date().toISOString()));
  };
  const markLastCandidateDelivery = (deliveryStatus) => {
    setMessages((prev) => updateLastCandidate(prev, { deliveryStatus }));
  };
  // Replace the most recent candidate bubble with the server's authoritative text (Whisper) so the
  // displayed caption always matches the stored transcript (Phase F — single source of truth).
  const setLastCandidateText = (text) => {
    if (!text || !text.trim()) return;
    setMessages((prev) => updateLastCandidate(prev, { text }));
  };

  // ── live AI delivery: reveal the interviewer bubble word-by-word in time with the voice ──
  const revealRef = useRef(null);
  const stopReveal = () => { if (revealRef.current) { clearInterval(revealRef.current); revealRef.current = null; } };
  const setLastInterviewerText = (text) => {
    setMessages((prev) => {
      for (let i = prev.length - 1; i >= 0; i -= 1) {
        if (prev[i].role === 'interviewer') {
          if (prev[i].text === text) return prev;
          const next = [...prev]; next[i] = { ...next[i], text }; return next;
        }
      }
      return prev;
    });
  };
  const revealInterviewer = (full, durationSec) => {
    stopReveal();
    const words = (full || '').split(/(\s+)/);   // keep whitespace tokens so spacing is preserved
    const total = words.length;
    if (!total) return;
    const ms = Math.max(700, (durationSec || 0) * 1000);
    const start = performance.now();
    revealRef.current = setInterval(() => {
      const frac = Math.min(1, (performance.now() - start) / ms);
      setLastInterviewerText(words.slice(0, Math.max(1, Math.round(total * frac))).join(''));
      if (frac >= 1) { stopReveal(); setLastInterviewerText(full); }
    }, 60);
  };
  // Speak a line while progressively revealing its bubble (empty bubble → grows → snaps to full text).
  async function speakRevealing(text) {
    text = spokenText(text);
    if (text) setMessages((prev) => [...prev, { role: 'interviewer', text: '', ts: new Date().toISOString() }]);
    setAiState('speaking');
    try {
      await speakAi(text, { onStart: (dur) => revealInterviewer(text, dur) });
    } finally {
      stopReveal();
      if (text) setLastInterviewerText(text);
    }
  }

  // ── teardown: stop only THIS page's machinery (recognizer/speech/timers). ──
  // Camera/mic/screen + recording are owned by MediaProvider and must survive this page
  // mounting/unmounting (incl. StrictMode's dev double-mount). The provider releases them on
  // its own unmount; the interview end (onFinished) releases them explicitly.
  const teardown = useCallback(() => {
    pageLifetimeRef.current += 1;
    joinAttemptRef.current?.cancel();
    recognitionEpochRef.current += 1;
    clearSilence();
    clearInactivity();
    clearVadTimers();
    clearInputActivity(false);
    listeningRef.current = false;
    recordingRef.current = false;
    try { recognizerRef.current && recognizerRef.current.abort(); } catch { /* ignore */ }
    try { stopSpeaking(); } catch { /* ignore */ }
    try { stopAiVoice && stopAiVoice(); } catch { /* ignore */ }
    stopReveal();
  }, [stopAiVoice]);
  const teardownRef = useRef(teardown);
  teardownRef.current = teardown;
  useEffect(() => () => teardownRef.current && teardownRef.current(), []);

  // local 1s elapsed ticker while on the call
  useEffect(() => {
    if (phase !== 'call') return undefined;
    const id = setInterval(() => { if (!endedRef.current) setElapsed((p) => (p == null ? p : p + 1)); }, 1000);
    return () => clearInterval(id);
  }, [phase]);

  useEffect(() => {
    if (!activeTask || phase !== 'call') return undefined;
    clearSilence();
    clearInactivity();
    stopListening();
    setAiState('idle');
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTask, phase]);

  useEffect(() => {
    if (!activeTask || activeTask.status !== 'active' || phase !== 'call') return undefined;
    const id = setInterval(() => {
      setActiveTask((prev) => {
        if (!prev || prev.status !== 'active') return prev;
        return { ...prev, remaining_seconds: Math.max(0, Number(prev.remaining_seconds || 0) - 1) };
      });
    }, 1000);
    return () => clearInterval(id);
  }, [activeTask, phase]);

  useEffect(() => {
    if (!activeTask || activeTask.status !== 'active' || phase !== 'call') return undefined;
    if (Number(activeTask.remaining_seconds || 0) > 0) return undefined;
    if (taskSubmittingRef.current) return undefined;
    submitPracticalTask(taskAnswerRef.current.trim() ? 'auto_submitted' : 'timed_out', false);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTask?.remaining_seconds, activeTask?.status, phase]);

  // ── server-authority poll ──
  // The backend ends the interview on its own when the time budget (+grace) is reached or the candidate
  // goes silent — even if this tab is idle or backgrounded. This poll surfaces that autonomous end so the
  // candidate lands on the result screen instead of a stuck "live" view that only updates on their next turn.
  useEffect(() => {
    if (phase !== 'call') return undefined;
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      if (cancelled || endedRef.current) return;
      try {
        const st = await apiRef.current.status();
        const wasPreparing = preparationActive(preparationRef.current.status);
        if (st && !st.finished) {
          const preparationReady = ['ready', 'ready_degraded'].includes(
            String(st.preparation_status || '').toLowerCase(),
          );
          if (wasPreparing && preparationReady && !preparationGreetingHandledRef.current) {
            preparationGreetingHandledRef.current = true;
            busyRef.current = true;
            await handleEngineResponse(st);
          } else {
            applyTurnState(st);
          }
        }
        // Wait for any in-progress AI speech to finish before speaking the closing — otherwise a time-up
        // that lands while Wiza is still voicing the previous answer makes her speak both at once. The
        // poll keeps ticking, so the closing fires on the next tick once the current line ends (even a few
        // seconds past the grace period). `busyRef` likewise defers it while a turn is still being processed.
        if (st && st.finished && !endedRef.current && !concludingRef.current
            && !speakingAudioRef.current && !busyRef.current) {
          // The server ended it on its own (silence / time budget). Speak the AI's final closing message
          // and hold for the closing-display window, exactly like a normal end — don't snap to the result.
          if (st.closing_display_seconds != null) closeDelayRef.current = st.closing_display_seconds;
          await concludeWithClosing(st.interviewer_message);
        }
      } catch { /* best-effort — the next tick retries */ }
      if (!cancelled && !endedRef.current) {
        const delay = preparationActive(preparationRef.current.status) ? PREPARATION_POLL_MS : POLL_MS;
        timer = setTimeout(poll, delay);
      }
    };
    timer = setTimeout(poll, preparationActive(preparationRef.current.status) ? PREPARATION_POLL_MS : POLL_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, sessionId, candidateId]);

  // ── presence: pause the server silence monitor while the candidate is away ──
  // Leaving the page (tab hidden / switching away) or unloading (refresh / close) signals "away" so the
  // "are you still there?" nudges + auto-end pause — a network blip / reconnect window isn't penalised.
  // Returning resumes the clock. The rejoin itself (a fresh page load that restores the interview) is
  // counted separately in onJoin.
  useEffect(() => {
    if (phase !== 'call') return undefined;
    const onVisibility = () => {
      if (endedRef.current) return;
      if (document.hidden) apiRef.current.presenceBeacon(false);       // beacon: survives an unload
      else apiRef.current.presence(true).catch(() => {});              // back on the page → resume clock
    };
    const onPageHide = () => { if (!endedRef.current) apiRef.current.presenceBeacon(false, { unloading: true }); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, sessionId, candidateId]);

  // ── captured-speech endpointing ──
  function clearSilence() {
    if (silenceRef.current) { clearTimeout(silenceRef.current); silenceRef.current = null; }
  }

  // ── inactivity check-in: Wiza nudges if the candidate stays silent on their turn ──
  function clearInactivity() {
    if (inactivityRef.current) { clearTimeout(inactivityRef.current); inactivityRef.current = null; }
  }
  function armInactivity() {
    if (inactivityRef.current) return;               // idempotent: survives Chrome's recognizer churn
    inactivityRef.current = setTimeout(handleInactivity, nudgeIntervalRef.current);
  }
  async function handleInactivity() {
    inactivityRef.current = null;
    if (endedRef.current || busyRef.current) return;
    if (activeTaskRef.current) return;
    // Silence is tracked whether the mic is live OR muted — a muted candidate is still silent, so the
    // check-in/auto-end must NOT depend on the mic being on (the "it only nudged while the mic was on" bug).
    const mutedNow = mutedRef.current;
    if (!listeningRef.current && !mutedNow) return;
    if (!mutedNow && captionRef.current.trim()) return;   // they're actually mid-speech
    // Exhausted the configured check-ins with continued silence → end the interview (don't nudge forever).
    if (nudgeCountRef.current >= nudgeMaxRef.current) {
      stopListening();
      await endForInactivity();
      return;
    }
    const line = NUDGES[Math.min(nudgeCountRef.current, NUDGES.length - 1)];
    nudgeCountRef.current += 1;
    stopListening();                                 // pause mic so Wiza doesn't hear herself
    addMsg('interviewer', line);
    setAiState('speaking');
    try { await speakAi(line); } catch { /* ignore */ }
    if (endedRef.current || busyRef.current) return;
    // Resume listening if the mic is live; if muted, keep the silence clock running so the next check-in
    // (and eventually the auto-end) still fires.
    if (mutedRef.current) { setAiState('idle'); armInactivity(); }
    else startListening();
  }
  // Continued silence after the last check-in → end server-side, then speak the closing and hold (NOT a
  // hard cut). endInterview returns the closing "…take care!" line in interviewer_message.
  async function endForInactivity() {
    if (endedRef.current || concludingRef.current) return;
    let resp = null;
    try { resp = await apiRef.current.end('abandoned_inactivity'); }
    catch { /* ignore — still conclude gracefully */ }
    await concludeWithClosing(resp && resp.interviewer_message);
  }

  // Graceful finish shared by every AUTONOMOUS end (frontend inactivity OR the server-authority poll):
  // speak the AI's final closing message, then run the normal closing hold → result. The turn-driven
  // finish in handleEngineResponse already does this; this makes a silence/time end behave identically
  // instead of snapping straight to the result page.
  async function concludeWithClosing(message) {
    if (endedRef.current || concludingRef.current) return;
    concludingRef.current = true;                    // a later poll tick must not re-enter mid-closing
    windingRef.current = true; setWinding(true);      // wrap-up: stop enforcing screen share (no re-share nag)
    clearInactivity(); clearSilence(); stopListening();
    // Release the CAMERA the instant the interview is terminal — not after the spoken closing (which can
    // run several seconds), so the webcam light goes off immediately. Camera-only: `stopAll()` (in
    // onFinished) also closes the AI voice bus we still need to speak the closing line.
    try { camera.stop(); } catch { /* ignore */ }
    try { screen.stop(); } catch { /* ignore */ }
    setShareWarning('');
    try { stopAiVoice && stopAiVoice(); } catch { /* ignore — cut any in-flight speech first */ }
    if (message) {
      setAiState('speaking');
      try { await speakRevealing(message); } catch { /* ignore */ }
    }
    onFinished();                                    // sets endedRef + the closing-display countdown → result
  }

  // Heartbeat: tell the backend the candidate is actively engaged (speaking/typing) so its silence monitor
  // doesn't fire "are you still there?" while they're mid-answer (no /turn arrives until the utterance is
  // submitted). Throttled; reuses /presence, which resets the backend silence clock. Best-effort.
  function touchBackend() {
    if (endedRef.current) return;
    const now = Date.now();
    if (now - lastTouchRef.current < 20000) return;   // at most once per 20s
    lastTouchRef.current = now;
    apiRef.current.presence(true).catch(() => {});
  }

  // ── listening (Web Speech captions + PCM recording) ──
  function clearInputActivity(updateState = true) {
    inputActiveRef.current = false;
    if (updateState) setInputActive(false);
  }

  function pulseInputActivity() {
    if (!inputActiveRef.current) {
      inputActiveRef.current = true;
      setInputActive(true);
    }
  }

  function saveBrowserCaption() {
    return uploadRef.current?.saveCaption(interimCaptionRef.current || captionRef.current,
      finalCaptionRef.current, captionSourceRef.current, recognitionDiagnosticsRef.current)
      .catch(() => warnAudio('Browser captions could not be saved on this device. Keep this tab open and finish your answer, or type instead.'));
  }

  // Shared recognizer transcript handler — reused by startListening AND the resume in handleRecEnd, so a
  // resumed recognizer keeps feeding the same caption/activity/silence logic.
  function onRecTranscript(text, isFinal, finalText) {
    recognitionDiagnosticsRef.current = { ...recognitionDiagnosticsRef.current,
      result_count: (recognitionDiagnosticsRef.current.result_count || 0) + 1,
      last_result_at: Date.now() };
    if (listeningRef.current && text.trim() && text.trim() !== lastCaptionResultRef.current) {
      lastCaptionActivityRef.current = Date.now();
      lastCaptionResultRef.current = text.trim();
      armPcmFallbackFinalize(true);
    }
    setCaption(text); captionRef.current = text;
    finalCaptionRef.current = finalText || finalCaptionRef.current;
    if (text.trim()) interimCaptionRef.current = text;
    saveBrowserCaption();
    if (listeningRef.current && text.trim()) pulseInputActivity();
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[AI Interview STT] transcript', {
        words: text.trim().split(/\s+/).filter(Boolean).length,
      });
    }
    // Real speech = activity: reset the resume counter (so the error-loop guard only trips on dead air)
    // and the backend check-in clock. A one-word blip (background noise) must NOT reset the clock, or the
    // "are you still there?" nudge never matures.
    if (text.trim().split(/\s+/).filter(Boolean).length >= 2
        && capture.getMetrics().speechRms >= 0.005) {
      recResumeRef.current = 0; clearInactivity(); touchBackend();
    }
    // Captions are diagnostic. Only sustained captured speech owns endpointing.
  }

  function pcmDurationMs(chunks = null) {
    const bytes = chunks ? chunks.reduce((total, chunk) => total + (chunk ? chunk.byteLength : 0), 0)
      : recordedBytesRef.current;
    return Math.round((bytes / 2 / 16000) * 1000);
  }

  function clearVadTimers() {
    if (vadFinalizeRef.current) { clearTimeout(vadFinalizeRef.current); vadFinalizeRef.current = null; }
    if (vadMaxRef.current) { clearTimeout(vadMaxRef.current); vadMaxRef.current = null; }
  }

  function resetPcmVoiceActivity() {
    clearVadTimers();
    vadSpeechMsRef.current = 0;
    vadActiveMsRef.current = 0;
    vadLastSpeechAtRef.current = 0;
    vadCandidateAtRef.current = 0;
    vadBurstMsRef.current = 0;
    vadDebugAtRef.current = 0;
    lastCaptionActivityRef.current = 0;
    lastCaptionResultRef.current = '';
  }

  function armPcmFallbackFinalize(allowCaptionActivity = false) {
    if (vadFinalizeRef.current) clearTimeout(vadFinalizeRef.current);
    vadFinalizeRef.current = setTimeout(() => {
      vadFinalizeRef.current = null;
      if (busyRef.current || endedRef.current || speakingAudioRef.current
          || (!recordingRef.current && !mutedRef.current)) return;
      if (vadSpeechMsRef.current < PCM_VAD_MIN_SPEECH_MS
          && !(allowCaptionActivity && captionRef.current.trim() && pcmDurationMs() >= 500)) return;
      if (voiceSilenceRemaining(Date.now(), vadLastSpeechAtRef.current, lastCaptionActivityRef.current) > 0) {
        armPcmFallbackFinalize(allowCaptionActivity);
        return;
      }
      finalizeVoiceTurn('silence');
    }, Math.max(1, Math.min(PCM_VAD_SILENCE_MS,
      voiceSilenceRemaining(Date.now(), vadLastSpeechAtRef.current, lastCaptionActivityRef.current)
      || PCM_VAD_SILENCE_MS)));
  }

  function handleCandidatePcmFrame(buf, signal = {}) {
    if (!recordingRef.current || mutedRef.current || speakingAudioRef.current) return;
    recordedBytesRef.current += buf.byteLength;
    if (uploadRef.current) uploadRef.current.addFrame(buf);
    else recChunksRef.current.push(buf);
    const voiceLike = signal.detectionSpeechLike ?? signal.rawSpeechLike ?? pcmFrameHasVoice(buf, capture.getMetrics().noiseFloor);
    if (!voiceLike) return;
    pulseInputActivity();

    const frameMs = Math.round((buf.byteLength / 2 / 16000) * 1000);
    vadActiveMsRef.current += frameMs;
    const now = Date.now();
    const sinceCandidate = vadCandidateAtRef.current ? now - vadCandidateAtRef.current : 0;
    if (sinceCandidate > PCM_VAD_BURST_RESET_MS) vadBurstMsRef.current = 0;
    vadBurstMsRef.current += Math.max(1, frameMs);
    vadCandidateAtRef.current = now;
    vadSpeechMsRef.current = Math.max(vadSpeechMsRef.current, vadBurstMsRef.current);
    if (vadBurstMsRef.current >= PCM_VAD_MIN_SPEECH_MS) {
      vadLastSpeechAtRef.current = now;
      clearInactivity();
      touchBackend();
      armPcmFallbackFinalize();
    }
  }

  function checkNoCaptionTurn() {
    vadMaxRef.current = null;
    if (busyRef.current || endedRef.current || speakingAudioRef.current || !recordingRef.current) return;
    if (captionRef.current.trim()) return;
    const detectedMs = vadSpeechMsRef.current;
    if (detectedMs < PCM_VAD_MIN_SPEECH_MS) {
      // Quiet speech may not trigger the local detector. Keep its PCM and the
      // question pending; the candidate can finish manually or type.
      vadMaxRef.current = setTimeout(checkNoCaptionTurn, PCM_VAD_NO_SPEECH_RESTART_MS);
      return;
    }
    const pcmMs = pcmDurationMs();
    const lastVoiceAgoMs = Date.now() - vadLastSpeechAtRef.current;
    if (vadSpeechMsRef.current < PCM_VAD_MIN_SPEECH_MS && lastVoiceAgoMs >= PCM_VAD_SILENCE_MS) {
      finalizeVoiceTurn('silence');
      return;
    }
    if (shouldFinalizeSparseNoCaption(pcmMs, vadActiveMsRef.current, lastVoiceAgoMs)) {
      console.info('[AI Interview STT] sparse no-caption turn ending', {
        pcm_ms: pcmMs, detected_ms: vadActiveMsRef.current,
      });
      if (voiceSilenceRemaining(Date.now(), vadLastSpeechAtRef.current, lastCaptionActivityRef.current) > 0) {
        vadMaxRef.current = setTimeout(checkNoCaptionTurn, 2000);
        return;
      }
      finalizeVoiceTurn('silence');
      return;
    }
    vadMaxRef.current = setTimeout(checkNoCaptionTurn, 2000);
  }

  function handleRecError(event = {}) {
    const error = event.error || 'unknown';
    const details = {
      error,
      message: event.message || '',
      type: event.type || '',
      source: event.source || '',
      caption_words: captionRef.current.trim().split(/\s+/).filter(Boolean).length,
      pcm_ms: pcmDurationMs(),
      listening: listeningRef.current,
      muted: mutedRef.current,
      busy: busyRef.current,
    };
    console.warn('[AI Interview STT] browser recognition issue', details);
    if (/audio-capture|not-allowed|service-not-allowed|network|start_failed/i.test(error)) {
      warnAudio('Browser captions stopped. Your microphone audio is still recording; finish speaking or type your answer.', 'captions');
    }
  }

  function createInterviewRecognizer() {
    const epoch = recognitionEpochRef.current;
    recognitionDiagnosticsRef.current = { ...recognitionDiagnosticsRef.current,
      started_at: Date.now(), sessions: (recognitionDiagnosticsRef.current.sessions || 0) + 1 };
    return createRecognizer(
      (...args) => { if (epoch === recognitionEpochRef.current) onRecTranscript(...args); },
      (event) => { if (epoch === recognitionEpochRef.current) handleRecEnd(event); },
      'en-US', (event) => { if (epoch === recognitionEpochRef.current) handleRecError(event); });
  }

  function startListening({ resume = false } = {}) {
    if (recoveryManualRef.current || recoveryStateRef.current === 'checking') return;
    if (mutedRef.current) { setAiState('idle'); return; }   // muted → never capture the candidate
    if (activeTaskRef.current) { setAiState('idle'); return; }
    if (screenBlocked()) { setAiState('idle'); return; }    // screen share dropped → paused until re-shared
    if (speakingAudioRef.current) return;                   // single-turn: never re-arm WHILE Wiza's audio plays
                                                            // (releases the instant speech ends, so the re-arm proceeds)
    if (listeningRef.current || endedRef.current || busyRef.current || textModeRef.current) return;
    if (capture.getStream()?.getAudioTracks()[0]?.readyState !== 'live') {
      setAiState('idle');
      warnAudio('Your microphone is disconnected. Turn it on again, or type your answer.', 'device');
      return;
    }
    if (!isMock && uploadRef.current && !resume) return;
    if (!resume) {
      recChunksRef.current = [];
      recordedBytesRef.current = 0;
      finalCaptionRef.current = '';
      interimCaptionRef.current = '';
      recognitionDiagnosticsRef.current = {};
      resetPcmVoiceActivity();
      capture.resetTurnMetrics();
      captionSourceRef.current = 'unknown';
      recResumeRef.current = 0;
    }
    recognitionEpochRef.current += 1;
    if (!isMock && !uploadRef.current) {
      uploadRef.current = new VoiceUploadManager(sessionId.trim(), candidateId.trim(), apiRef.current);
      uploadRef.current.turn.recovery = { questionKey: questionKeyRef.current, count: clarificationCountRef.current };
      uploadRef.current.start().catch(() => {
        warnAudio('Audio recovery storage is unavailable. You can type your answer.');
      });
    }
    recordingRef.current = true;                     // capture.onFrame starts appending
    let rec = null;
    try { rec = createInterviewRecognizer(); } catch { /* PCM recording remains available */ }
    recognizerRef.current = rec;
    listeningRef.current = true;
    setAiState('listening');
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[AI Interview STT] listening started');
    }
    if (rec) {
      let started = false;
      try { started = rec.start(resume ? finalCaptionRef.current : '', capture.getStream()?.getAudioTracks()[0]); }
      catch { /* PCM recording remains available */ }
      if (started) {
        captionSourceRef.current = rec.getAudioSource();
      } else {
        recognizerRef.current = null;
        warnAudio('Browser captions are unavailable. Your microphone audio is still recording; you can finish speaking or type.', 'captions');
      }
    } else {
      warnAudio('Browser captions are unavailable. Your microphone audio is still recording; you can finish speaking or type.', 'captions');
    }
    if (!listeningRef.current) return;
    if (resume && vadLastSpeechAtRef.current) armPcmFallbackFinalize();
    vadMaxRef.current = setTimeout(checkNoCaptionTurn, PCM_VAD_NO_SPEECH_RESTART_MS);
    armInactivity();                                 // start the "are you there?" clock
  }

  function stopListening() {
    clearSilence();
    clearInactivity();
    clearVadTimers();
    clearInputActivity();
    listeningRef.current = false;
    recordingRef.current = false;
    try { recognizerRef.current && recognizerRef.current.stop(); } catch { /* ignore */ }
  }

  function pauseListeningForMute() {
    clearInactivity();
    if (vadMaxRef.current) { clearTimeout(vadMaxRef.current); vadMaxRef.current = null; }
    listeningRef.current = false;
    recordingRef.current = false;
    clearInputActivity();
    const snapshot = recognizerRef.current?.getSnapshot?.();
    if (snapshot?.finalText) finalCaptionRef.current = snapshot.finalText;
    saveBrowserCaption();
    recognitionEpochRef.current += 1;
    try { recognizerRef.current?.abort(); } catch { /* ignore */ }
    recognizerRef.current = null;
    if (vadLastSpeechAtRef.current || (finalCaptionRef.current && recordedBytesRef.current)) {
      armPcmFallbackFinalize(true);
    }
  }

  // A browser recognition boundary is not an answer boundary. Keep captions
  // running even after a complete sentence; PCM silence owns turn completion.
  function handleRecEnd(event = {}) {
    recognitionDiagnosticsRef.current = {
      ...recognitionDiagnosticsRef.current,
      source: event.source || 'end', error: event.error || '', resumes: recResumeRef.current,
      ended_at: Date.now(),
    };
    saveBrowserCaption();
    if (!listeningRef.current) return;
    if (activeTaskRef.current) { stopListening(); return; }
    const cap = captionRef.current.trim();
    const fatalRecognitionError = /audio-capture|not-allowed|service-not-allowed|start_failed/i.test(event.error || '');
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[AI Interview STT] recognition ended', {
        source: event.source || 'end',
        error: event.error || '',
        caption_words: cap ? cap.split(/\s+/).filter(Boolean).length : 0,
        pcm_ms: pcmDurationMs(),
        resumes: recResumeRef.current,
      });
    }
    if (cap && !vadLastSpeechAtRef.current && pcmDurationMs() >= 500 && !vadFinalizeRef.current) {
      // Browser activity can reveal quiet speech missed by raw VAD. It may trigger
      // server analysis, but only server STT or a final caption can create a turn.
      armPcmFallbackFinalize(true);
    }
    if (!fatalRecognitionError && recResumeRef.current < 12
        && !endedRef.current && !busyRef.current && !mutedRef.current && !screenBlocked()) {
      const rec = createInterviewRecognizer();
      if (rec) {
        recResumeRef.current += 1;
        recognizerRef.current = rec;
        if (!rec.start(finalCaptionRef.current, capture.getStream()?.getAudioTracks()[0])) {
      warnAudio('Browser speech recognition stopped. Your audio is still available; finish speaking or type your answer.', 'captions');
          return;
        }
        if (!listeningRef.current) return;
        if (rec.getAudioSource() === 'default_microphone') captionSourceRef.current = 'default_microphone';
        return;
      }
    }
    if (cap) {
      if (vadLastSpeechAtRef.current) {
        if (!vadFinalizeRef.current) armPcmFallbackFinalize();
      }
      return;
    }
    if (vadSpeechMsRef.current >= PCM_VAD_MIN_SPEECH_MS || recordedBytesRef.current) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[AI Interview STT] browser recognizer ended without caption; keeping PCM fallback active', {
          pcm_ms: pcmDurationMs(),
          vad_speech_ms: vadSpeechMsRef.current,
        });
      }
      if (vadSpeechMsRef.current >= PCM_VAD_MIN_SPEECH_MS) return;
    }
    if (fatalRecognitionError) {
      recognizerRef.current = null;
      warnAudio('Browser captions stopped. Your microphone audio is still recording; finish speaking or type your answer.', 'captions');
      return;
    }
    warnAudio('Browser speech recognition stopped. Your audio is still available; finish speaking or type your answer.', 'captions');
  }

  function concatPcm(chunks) {
    let total = 0;
    for (const c of chunks) total += c.byteLength;
    const out = new Uint8Array(total);
    let off = 0;
    for (const c of chunks) { out.set(new Uint8Array(c), off); off += c.byteLength; }
    return out.buffer;
  }

  // ── one engine turn ──
  const applyTurnState = useCallback((d) => {
    if (Object.prototype.hasOwnProperty.call(d, 'final_qa')) finalQaRef.current = d.final_qa;
    const nextPreparation = {
      status: d.preparation_status || '',
      stage: d.preparation_stage || '',
      substage: d.preparation_substage || '',
      stageHistory: Array.isArray(d.preparation_stage_history) ? d.preparation_stage_history : [],
      attempt: Number(d.preparation_attempt || 0),
      retryCount: Number(d.preparation_retry_count || 0),
      degradedReasons: Array.isArray(d.preparation_degraded_reasons) ? d.preparation_degraded_reasons : [],
      error: d.preparation_error || '',
      hasResume: typeof d.preparation_has_resume === 'boolean' ? d.preparation_has_resume : null,
      stageUpdatedAt: d.preparation_stage_updated_at || '',
      questionProgress: d.preparation_question_progress || null,
    };
    preparationRef.current = nextPreparation;
    setPreparation(nextPreparation);
    if (Object.prototype.hasOwnProperty.call(d, 'current_stage')) {
      setStage(d.active_practical_task ? 'Practical Task' : (d.current_stage || ''));
    }
    if (Object.prototype.hasOwnProperty.call(d, 'topic_progress')) {
      setTopicProgress(d.topic_progress || null);
    }
    if (d.role_title || d.jd_role_title) setRoleTitle(d.role_title || d.jd_role_title);
    if (d.active_practical_task) {
      if (!activeTaskRef.current) {
        setTaskAnswer('');
        setTaskIntegrity({ paste_count: 0, pasted_characters: 0, large_insert_count: 0 });
      }
      setActiveTask(d.active_practical_task);
      setTaskConfirmOpen(false);
    } else if (activeTaskRef.current && d.turn_type !== 'practical_task') {
      setActiveTask(null);
      setTaskConfirmOpen(false);
    }
    if (d.closing_display_seconds != null) closeDelayRef.current = d.closing_display_seconds;
    if (d.interview_elapsed_seconds != null) setElapsed(d.interview_elapsed_seconds);
    // Inactivity check-in config (single source of truth = backend): interval + how many before ending.
    if (d.nudge_after_seconds != null) nudgeIntervalRef.current = d.nudge_after_seconds * 1000;
    if (d.nudge_count != null) nudgeMaxRef.current = d.nudge_count;
    if (d.rejoin_min_seconds != null) rejoinMinRef.current = d.rejoin_min_seconds;
    setProgress({
      asked: d.questions_asked ?? 0,
      target: d.target_questions ?? null,
      total: d.duration_minutes != null ? d.duration_minutes * 60 : null,
    });
  }, []);

  async function speakThenListen(text) {
    // Single-turn: KEEP the turn locked (busyRef true) for the whole reply — through the spoken line —
    // and release it only just before we re-arm the mic. Previously busyRef was cleared here, at the
    // START of the reply, leaving a window where a stray restart or a second utterance became a second
    // turn (the double-answer). Release must precede startListening (which bails while busyRef is true).
    nudgeCountRef.current = 0;                        // fresh question → reset check-in escalation
    setCaption(''); captionRef.current = '';
    await speakRevealing(text);                       // AI is SPEAKING here — turn stays locked
    if (endedRef.current) return;
    busyRef.current = false;                          // reply delivered → now safe to accept the next turn
    if (pendingMicRestartRef.current && !(await recoverMicrophone())) {
      setAiState('idle');
      return;
    }
    startListening();
  }

  async function recoverMicrophone() {
    if (endedRef.current || phaseRef.current !== 'call' || micRecoveringRef.current) return false;
    const lifetime = pageLifetimeRef.current;
    micRecoveringRef.current = true;
    stopListening();
    setCaption(''); captionRef.current = '';
    try {
      await restartMic();
      if (lifetime !== pageLifetimeRef.current || endedRef.current || windingRef.current) return false;
      setFrameSink((buf, signal) => handleCandidatePcmFrame(buf, signal));
      pendingMicRestartRef.current = false;
      return true;
    } catch {
      if (lifetime !== pageLifetimeRef.current) return false;
      warnAudio('We could not reconnect your microphone. Check device or browser settings, then retry. Refreshing will resume this interview; you can also type your answer.', 'device');
      return false;
    } finally {
      micRecoveringRef.current = false;
    }
  }

  async function retryAudio() {
    if (busyRef.current || speakingAudioRef.current) return;
    if (!isMock && uploadRef.current) {
      const upload = uploadRef.current;
      if (uploadRejectedRef.current) {
        await upload.discard().catch(() => {});
        uploadRef.current = null;
        uploadRejectedRef.current = false;
        uploadCanTypeRef.current = false;
      } else {
        beginVoiceOperation();
        let accepted = false;
        try {
          const resp = upload.turn.finalizing
            ? await upload.reconcile()
            : await upload.finalizeInterrupted(Math.max(turnSeqRef.current + 1, upload.turn.clientTurnId || 0));
          uploadRef.current = null;
          uploadCanTypeRef.current = false;
          turnSeqRef.current = Math.max(turnSeqRef.current, resp.last_client_turn_id || 0);
          clearAudioWarning();
          markLastCandidateDelivery('sent');
          accepted = true;
          await handleEngineResponse(resp);
          return;
        } catch (error) {
          if (accepted) {
            settleVoiceOperation();
            warnAudio('Your answer was received. Read the current question and continue, or retry the microphone.');
            startListening();
            return;
          }
          if (await handleVoiceFailure(error)) return;
          settleVoiceOperation('checking');
          warnAudio('This answer is still saved here. Retry delivery or type after the connection recovers.');
          showError(error.message || 'Delivery could not be confirmed.');
          return;
        } finally { busyRef.current = false; }
      }
    }
    recoveryManualRef.current = false;
    settleVoiceOperation();
    const ok = await recoverMicrophone();
    if (ok) startListening();
  }

  async function recordAgain() {
    if (busyRef.current || speakingAudioRef.current || recoveryStateRef.current === 'checking') return;
    stopListening();
    beginVoiceOperation();
    try {
      const upload = uploadRef.current;
      if (upload) {
        if ((upload.turn.finalizing || restoredUploadRef.current === upload) && !upload.turn.lastFailure) {
          settleVoiceOperation('checking');
          warnAudio('Check delivery of your saved answer before starting another recording.');
          return;
        }
        upload.flush();
        await withVoiceDeadline(upload.archiveForRepeat({ questionKey: questionKeyRef.current,
          count: clarificationCountRef.current }));
        if (uploadRef.current === upload) uploadRef.current = null;
      }
      recoveryManualRef.current = false;
      textModeRef.current = false;
      setTextMode(false);
      settleVoiceOperation('repeat');
      applyMicMuted(false);
      if (await recoverMicrophone()) startListening();
    } catch {
      recoveryManualRef.current = true;
      settleVoiceOperation('manual');
      warnAudio('The previous recording is still pending. Please retry recovery before replacing it.');
    }
  }

  async function handleVoiceFailure(error) {
    const failure = describeVoiceFailure(error);
    if (!failure) return false;
    if (recoveryHandlingRef.current) return true;
    recoveryHandlingRef.current = true;
    stopListening();
    settleVoiceOperation('repeat');
    uploadRejectedRef.current = !failure.keepAudio;
    uploadCanTypeRef.current = true;
    markLastCandidateDelivery('not_sent');
    try {
      const upload = uploadRef.current;
      const previous = upload?.turn.recovery || {};
      const alreadyPrompted = previous.clarificationFor === upload?.turn.utteranceId;
      clarificationCountRef.current = Math.max(clarificationCountRef.current, Number(previous.count) || 0);
      const noiseOnly = error.reason === 'noise_only' && error.recoveryAction === 'continue_listening';
      if (!alreadyPrompted && !noiseOnly) clarificationCountRef.current += 1;
      if (upload) {
        await withVoiceDeadline((async () => {
          await upload.rememberFailure(error);
          await upload.archiveForRepeat({ questionKey: questionKeyRef.current,
            count: clarificationCountRef.current, clarificationFor: upload.turn.utteranceId });
        })());
        if (uploadRef.current === upload) uploadRef.current = null;
      }
      if (noiseOnly) {
        setMessages((previousMessages) => {
          const last = previousMessages[previousMessages.length - 1];
          return last?.role === 'candidate' && last.deliveryStatus === 'not_sent'
            && /^[.\u2026\s]*$/.test(last.text || '')
            ? previousMessages.slice(0, -1) : previousMessages;
        });
        uploadRejectedRef.current = false;
        uploadCanTypeRef.current = false;
        clearAudioWarning();
        settleVoiceOperation('repeat');
        if (!endedRef.current && !textModeRef.current && !recoveryManualRef.current) startListening();
        return true;
      }
      const automatic = clarificationCountRef.current <= MAX_VOICE_CLARIFICATIONS;
      recoveryManualRef.current = !automatic;
      warnAudio(automatic ? 'Please say your answer again, or type it below. The previous recording is saved.'
        : 'We are still having trouble hearing you. Try recording again or type your answer. The recordings are saved.');
      if (!automatic) { settleVoiceOperation('manual'); return true; }
      if (!alreadyPrompted && !endedRef.current) {
        busyRef.current = true;
        setRecoveryState('clarifying');
        try { await withVoiceDeadline(speakRevealing(VOICE_REPEAT_PROMPT), 15000); }
        catch {
          stopSpeaking();
          stopAiVoice?.();
          speakingAudioRef.current = false;
        }
      }
      settleVoiceOperation('repeat');
      if (!endedRef.current && !textModeRef.current) {
        if (!pendingMicRestartRef.current || await recoverMicrophone()) startListening();
      }
      return true;
    } catch {
      recoveryManualRef.current = true;
      settleVoiceOperation('manual');
      warnAudio('Your answer is still pending. Retry saving the recording or type after recovery storage is available.');
      return true;
    } finally { recoveryHandlingRef.current = false; }
  }

  deliveryCheckRef.current = async () => {
    const upload = uploadRef.current;
    if (!upload || busyRef.current || endedRef.current) return;
    busyRef.current = true;
    let accepted = false;
    try {
      const status = await upload.inspectDelivery();
      if (status.committed && status.response) {
        const resp = await upload.accept(status.response);
        uploadRef.current = null;
        uploadCanTypeRef.current = false;
        markLastCandidateDelivery('sent');
        clearAudioWarning();
        accepted = true;
        await handleEngineResponse(resp);
      } else if (!status.processing && status.last_failure) {
        const error = Object.assign(new Error(status.last_failure.code || 'unclear_audio'), {
          status: status.last_failure.status, code: status.last_failure.code,
          reason: status.last_failure.reason, audioRetained: status.last_failure.audio_retained,
          recoveryAction: status.last_failure.recovery_action,
        });
        await handleVoiceFailure(error);
      } else {
        settleVoiceOperation('checking');
      }
    } catch {
      if (accepted) {
        settleVoiceOperation();
        warnAudio('Your answer was received. Read the current question and continue, or retry the microphone.');
        startListening();
      } else {
        settleVoiceOperation('checking');
      }
    }
    finally { busyRef.current = false; }
  };

  useEffect(() => {
    if (voiceRecovery !== 'checking' || phase !== 'call') return undefined;
    const id = setInterval(() => deliveryCheckRef.current?.(), 5000);
    return () => clearInterval(id);
  }, [voiceRecovery, phase]);

  useEffect(() => {
    if (phase !== 'call') return undefined;
    const track = capture.stream?.getAudioTracks()[0];
    const changed = (event) => {
      if (endedRef.current) return;
      pendingMicRestartRef.current = true;
      warnAudio('Your microphone connection changed. Check your device or browser microphone settings, then retry. You can also type your answer.', 'device');
      if (event.type === 'ended' && recordingRef.current && recordedBytesRef.current > 0 && !busyRef.current) {
        finalizeVoiceTurn('microphone_interruption');
        return;
      }
      if (!busyRef.current && !speakingAudioRef.current && !captionRef.current.trim()) recoverMicrophone().then((ok) => {
        if (ok) startListening();
      });
    };
    track?.addEventListener('ended', changed);
    navigator.mediaDevices?.addEventListener?.('devicechange', changed);
    return () => {
      track?.removeEventListener('ended', changed);
      navigator.mediaDevices?.removeEventListener?.('devicechange', changed);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, capture.stream]);

  async function handleEngineResponse(resp) {
    updateQuestionContext(resp);
    setRecoveryState('none');
    if (resp && resp.candidate_text) setLastCandidateText(resp.candidate_text);
    const enteringTask = resp && (resp.turn_type === 'practical_task' || resp.active_practical_task) && !activeTaskRef.current;
    applyTurnState(resp);
    if (preparationActive(resp?.preparation_status) || resp?.turn_type === 'preparing') {
      stopListening();
      clearSilence();
      clearInactivity();
      setCaption(''); captionRef.current = '';
      busyRef.current = false;
      setAiState('thinking');
      return;
    }
    if (String(resp?.preparation_status || '').toLowerCase() === 'failed') {
      stopListening();
      busyRef.current = false;
      setAiState('idle');
      return;
    }
    if (resp.finished) {
      // Same graceful close as an autonomous end — and setting concludingRef here stops the poll from
      // re-triggering the closing mid-speech.
      await concludeWithClosing(resp.interviewer_message);
      return;
    }
    if (resp && (resp.turn_type === 'practical_task' || resp.active_practical_task)) {
      stopListening();
      clearSilence();
      clearInactivity();
      setCaption(''); captionRef.current = '';
      if (enteringTask) {
        addMsg('interviewer', 'Next is a timed practical task. Complete it in the task window and submit there; the interview will continue after submission.');
      }
      busyRef.current = false;
      setAiState('idle');
      return;
    }
    await speakThenListen(resp.interviewer_message);
  }

  async function finalizeVoiceTurn(reason = 'manual_done') {
    clearSilence();
    clearVadTimers();
    if (busyRef.current || endedRef.current) return;
    if (activeTaskRef.current) return;
    if (speakingAudioRef.current) return;            // single-turn: ignore anything captured while Wiza's audio plays
    if (screenBlocked()) return;                     // screen share dropped → don't submit until re-shared
    beginVoiceOperation();
    const chunks = recChunksRef.current;
    const pcmMs = pcmDurationMs();
    const vadSpeechMs = Math.max(vadSpeechMsRef.current, vadActiveMsRef.current);
    const upload = uploadRef.current;
    const recognizer = recognizerRef.current;
    stopListening();
    // SpeechRecognition can deliver its final result just after stop(). Only
    // that final text, never interim text, is eligible for server fallback.
    const snapshot = await recognizer?.waitForFinal?.(1000);
    if (snapshot?.finalText) finalCaptionRef.current = snapshot.finalText;
    const fallback = (snapshot?.runningText || captionRef.current).trim();
    const finalCaption = finalCaptionRef.current.trim();
    const decision = isMock
      ? decideVoiceTurn(fallback, pcmMs, vadSpeechMs, PCM_VAD_MIN_AUDIO_MS, PCM_VAD_MIN_SPEECH_MS)
      : { submit: pcmMs > 0 || Boolean(finalCaption), usePcm: pcmMs > 0 };
    if (!decision.submit) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[AI Interview STT] ignored weak PCM fallback clip', { pcm_ms: pcmMs, vad_speech_ms: vadSpeechMs });
      }
      if (upload) await upload.discard().catch(() => {});
      uploadRef.current = null;
      setCaption(''); captionRef.current = '';
      busyRef.current = false;
      startListening();
      return;
    }
    addVoiceAttempt(isMock ? fallback : '');
    setCaption(''); captionRef.current = '';
    let accepted = false;
    try {
      const pcm = isMock && decision.usePcm ? concatPcm(chunks) : new ArrayBuffer(0);
      const metrics = capture.getMetrics();
      const diagnostics = {
        completion_reason: reason,
        last_pcm_speech_ago_ms: vadLastSpeechAtRef.current ? Date.now() - vadLastSpeechAtRef.current : null,
        last_caption_ago_ms: lastCaptionActivityRef.current ? Date.now() - lastCaptionActivityRef.current : null,
        raw_peak: Number(metrics.rawPeak?.toFixed(3)) || 0,
        raw_rms: Number(metrics.rawRms?.toFixed(3)) || 0,
        processed_peak: Number(metrics.processedPeak?.toFixed(3)) || 0,
        processed_rms: Number(metrics.processedRms?.toFixed(3)) || 0,
        raw_clipped: !!metrics.rawClipped,
        processed_clipped: !!metrics.processedClipped,
        track_rate: metrics.settings?.sampleRate || null,
        echo_cancellation: metrics.settings?.echoCancellation ?? null,
        noise_suppression: metrics.settings?.noiseSuppression ?? null,
        auto_gain_control: metrics.settings?.autoGainControl ?? null,
        device_hint: metrics.deviceHint || 'unknown',
      };
      const nextId = (turnSeqRef.current += 1);
      let resp;
      if (isMock) {
        resp = await apiRef.current.transcribe(pcm, fallback, nextId, captionSourceRef.current, diagnostics);
      } else {
        await withVoiceDeadline(upload.saveCaption(interimCaptionRef.current || fallback, finalCaption,
          captionSourceRef.current, recognitionDiagnosticsRef.current));
        resp = await upload.finalize(nextId, diagnostics);
        uploadRef.current = null;
      }
      accepted = true;
      uploadCanTypeRef.current = false;
      clearAudioWarning();
      markLastCandidateDelivery('sent');
      await handleEngineResponse(resp);
    } catch (e) {
      busyRef.current = false;
      if (accepted) {
        settleVoiceOperation();
        showError('The answer was received, but the interview response could not be shown. Please reconnect.');
        return;
      }
      if (await handleVoiceFailure(e)) {
        return;
      }
      settleVoiceOperation('checking');
      markLastCandidateDelivery('unconfirmed');
      warnAudio('Your answer is saved on this device, but delivery is not confirmed. Retry delivery or type after checking the connection.');
      showError(e.message || 'Delivery unconfirmed');
    }
  }

  // Wrap the text box's onChange so we can spot a paste: typing grows the value ~1 char at a time, a paste
  // inserts many at once. Flags the answer as pasted (integrity signal noted on the recruiter's result).
  const handleTypedChange = (next) => {
    const prev = lastTypedRef.current;
    if ((next || '').length - prev.length > 12) pastedRef.current = true;   // big jump = paste
    lastTypedRef.current = next || '';
    if ((next || '').trim()) {
      touchBackend();                 // typing is activity → keep the BACKEND silence clock reset
      // …and reset the FRONTEND inactivity clock too. Otherwise a long, silent typed answer (e.g. a code
      // example) matures the "are you still with me?" nudge — and eventually auto-ends the interview —
      // even though the candidate is actively typing. Mirrors onRecTranscript's reset for speech.
      // clearInactivity() first: armInactivity() is idempotent and returns early if a timer is pending.
      nudgeCountRef.current = 0;
      clearInactivity();
      if (listeningRef.current || mutedRef.current) armInactivity();
    }
    setTyped(next);
  };

  const handleTaskAnswerChange = (next) => {
    const prev = taskAnswerRef.current || '';
    if ((next || '').length - prev.length > 40) {
      setTaskIntegrity((cur) => ({
        ...cur,
        large_insert_count: Number(cur.large_insert_count || 0) + 1,
        pasted_characters: Number(cur.pasted_characters || 0) + Math.max(0, (next || '').length - prev.length),
      }));
    }
    setTaskAnswer(next);
  };

  const handleTaskPaste = (event) => {
    const pastedText = event?.clipboardData?.getData?.('text') || '';
    setTaskIntegrity((cur) => ({
      ...cur,
      paste_count: Number(cur.paste_count || 0) + 1,
      pasted_characters: Number(cur.pasted_characters || 0) + pastedText.length,
    }));
  };

  // text / skip turns go through the standard text seam (/turn)
  async function sendTextTurn(message, { show = true } = {}) {
    if (busyRef.current || endedRef.current) return;
    if (activeTaskRef.current) { showError('Please complete the practical task before continuing.'); return; }
    if (screenBlocked()) { showError('Please share your entire screen to continue.'); return; }
    if (!isMock && uploadRef.current) {
      if ((uploadRef.current.turn.finalizing || restoredUploadRef.current === uploadRef.current)
          && !uploadRejectedRef.current && !uploadCanTypeRef.current) {
        warnAudio('Confirm delivery of your saved voice answer before sending a typed answer.');
        return;
      }
      if (uploadRef.current.turn.lastFailure) {
        try { await uploadRef.current.archiveForRepeat(uploadRef.current.turn.recovery); }
        catch { settleVoiceOperation('manual'); warnAudio('The pending recording could not be preserved. Please retry.'); return; }
      } else await uploadRef.current.discard().catch(() => {});
      uploadRef.current = null;
      uploadRejectedRef.current = false;
      uploadCanTypeRef.current = false;
    }
    stopListening();
    busyRef.current = true;
    setAiState('thinking');
    const visible = show && message !== 'skip';
    if (visible) setMessages((prev) => appendTypedAttempt(prev, message, new Date().toISOString()));
    const wasPasted = pastedRef.current && message !== 'skip';   // capture before clearing
    pastedRef.current = false; lastTypedRef.current = '';
    setTyped('');
    let accepted = false;
    try {
      const resp = await apiRef.current.turn(message, wasPasted, (turnSeqRef.current += 1));
      accepted = true;
      clearAudioWarning();
      if (visible) markLastCandidateDelivery('sent');
      await handleEngineResponse(resp);
    } catch (e) {
      settleVoiceOperation();
      if (visible && !accepted) markLastCandidateDelivery('not_sent');
      showError(accepted ? 'The answer was received, but the interview response could not be shown. Please reconnect.' : (e.message || 'Turn failed'));
      if (!accepted && !textModeRef.current) startListening();
    }
  }

  async function submitPracticalTask(status = 'submitted', confirm = true) {
    const task = activeTaskRef.current;
    if (!task || taskSubmittingRef.current || endedRef.current) return;
    if (confirm) {
      setTaskConfirmOpen(true);
      return;
    }
    const answer = taskAnswerRef.current || '';
    const taskId = task.task_id || 'practical_task';
    const spent = task.duration_seconds != null && task.remaining_seconds != null
      ? Math.max(0, Number(task.duration_seconds || 0) - Number(task.remaining_seconds || 0))
      : null;
    setTaskConfirmOpen(false);
    setTaskSubmitting(true);
    taskSubmittingRef.current = true;
    stopListening();
    clearSilence();
    clearInactivity();
    busyRef.current = true;
    setAiState('thinking');
    try {
      const finalStatus = status === 'timed_out' && answer.trim() ? 'auto_submitted' : status;
      const resp = await apiRef.current.taskAnswer(
        taskId,
        answer,
        `${taskId}-${Date.now()}`,
        finalStatus,
        spent,
        taskIntegrityRef.current,
      );
      setActiveTask(null);
      setTaskAnswer('');
      setTaskIntegrity({ paste_count: 0, pasted_characters: 0, large_insert_count: 0 });
      await handleEngineResponse(resp);
    } catch (e) {
      busyRef.current = false;
      showError(e.message || 'Task submission failed');
    } finally {
      setTaskSubmitting(false);
      taskSubmittingRef.current = false;
    }
  }

  // ── setup → preflight lobby ──
  const handleStart = () => {
    if (!sessionId.trim() || !candidateId.trim()) {
      showError('Please enter both Session ID and Candidate ID.');
      return;
    }
    setShareWarning('');
    setPhase('preflight');
    if (RC.cameraRequired) {
      camera.start().catch(() => { /* candidate can retry via the Enable camera button */ });
    }
  };

  // ── preflight: enable camera ──
  const onEnableCamera = () => { camera.start().catch(() => { /* error surfaced via camera.error */ }); };

  // ── preflight / in-call: request entire-screen share (validated in the hook) ──
  const requestShare = async () => {
    if (windingRef.current || endedRef.current) return false;
    setShareWarning('');
    const r = await screen.start();
    if (r.reason === 'cancelled' || windingRef.current || endedRef.current) return false;
    if (!r.ok) {
      const msg = r.reason === 'not_entire_screen'
        ? 'Please share your ENTIRE screen (the whole monitor), not a single window or browser tab.'
        : r.reason === 'denied'
          ? 'Screen sharing was blocked. Allow it to continue.'
          : r.reason === 'unsupported'
            ? (r.error || "Screen sharing isn't available on phones or tablets — please open this interview on a laptop or desktop.")
            : r.reason === 'insecure'
              ? (r.error || 'Screen sharing needs a secure connection (https:// or localhost).')
              : 'Could not start screen sharing. Please try again.';
      if (phaseRef.current === 'call') showError(msg); else setShareWarning(msg);
      return false;
    }
    return true;
  };

  // Proctoring: ONLY when the entire-screen share is REQUIRED. If it stops mid-interview we pause turns
  // and surface a light inline banner (not a full-screen modal) that forces a re-share before continuing.
  // When screen sharing isn't required, drops are ignored entirely (screenBlocked stays false).
  const sharingRef = useRef(true);
  sharingRef.current = screen.sharing;
  // Enforce the entire-screen share only DURING the live interview — never once we're winding up/closing
  // (else stopping the share as the interview ends triggers a bogus "share again to continue" nag).
  const screenBlocked = () => phaseRef.current === 'call' && RC.screenRequired
    && !sharingRef.current && !windingRef.current && !endedRef.current;
  const onReshareDuringCall = async () => {
    const ok = await requestShare();   // re-acquire → MediaContext auto-resumes the screen recording
    if (ok && !endedRef.current && phaseRef.current === 'call') {
      startListening({ resume: !!uploadRef.current });
    }
  };
  // If the REQUIRED entire-screen share drops mid-interview, pause everything (mic, timers, AI voice)
  // — the inline banner below makes the candidate re-share before the interview continues. Skipped once
  // we're winding up (the closing message must not be cut by a share-drop at the end).
  useEffect(() => {
    if (phase !== 'call' || winding || !RC.screenRequired || screen.sharing) return;
    stopListening();
    clearSilence();
    clearInactivity();
    try { stopAiVoice && stopAiVoice(); } catch { /* ignore */ }
    stopReveal();
    setAiState('idle');
    setShareWarning('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen.sharing, phase, winding]);

  // ── preflight → join (actually start the interview) ──
  const onJoin = async () => {
    const lifetime = pageLifetimeRef.current;
    // Standalone preflight gates on media here; embedded entry was already gated (or the steps were
    // intentionally disabled in admin), so it must not silently bail — just start.
    if (!embedded) {
      if (RC.cameraRequired && !camera.stream) return;
      if (RC.screenRequired && !screen.sharing) return;
    }
    if (joinAttemptRef.current?.lifetime === lifetime) return;
    let rejectStartup;
    const cancelled = new Promise((_resolve, reject) => { rejectStartup = reject; });
    const attempt = { lifetime, cancel: () => rejectStartup(new DOMException('Interview startup cancelled', 'AbortError')) };
    joinAttemptRef.current = attempt;
    const watchdog = setTimeout(() => rejectStartup(Object.assign(
      new Error('Your interview could not open within 30 seconds. Please retry. Your saved answers are unchanged.'),
      { code: 'interview_start_timeout' },
    )), 30000);
    const waitForStartup = (operation) => Promise.race([operation, cancelled]);
    setJoining(true);
    setStartError('');
    if (rejoin) applyMicMuted(true);
    let resuming = rejoin;
    try {
      // Reopening the link (embedded candidate flow / explicit rejoin): if the latest run already ENDED
      // (e.g. the server closed it on time/inactivity while they were away), don't resume and don't mint a
      // fresh interview — show its result instead. Under time, the run is still unfinished → fall through
      // and rejoin/continue below. (Standalone dev starts are left free to begin a new run.)
      if (embedded || rejoin) {
        const prior = await waitForStartup(apiRef.current.status());
        if (lifetime !== pageLifetimeRef.current) return;
        resuming = resuming || Boolean(prior?.exists && !prior.finished);
        setIsRejoining(resuming);
        if (prior && prior.exists && prior.finished) {
          endedRef.current = true;
          try { stopAll(); } catch { /* ignore */ }
          setPhase('done');
          if (!embedded || isMock) await loadResult();   // mock seekers see their feedback on done
          return;
        }
      }
      applyMicMuted(resuming);
      if (RC.micRequired || sttSupported) {
        setMicStarting(true);
        try { await waitForStartup(startMic()); }
        catch (error) {
          if (error?.code === 'interview_start_timeout' || error?.name === 'AbortError') throw error;
          if (!resuming) throw error;
          warnAudio('Your microphone could not be reconnected. Turn it on again, or type your answer.', 'device');
        } finally { if (lifetime === pageLifetimeRef.current) setMicStarting(false); }
      }
      if (lifetime !== pageLifetimeRef.current) return;
      setFrameSink((buf, signal) => {
        // Single-turn: capture ONLY while genuinely listening AND the AI isn't speaking — so a stray/
        // resurrected recognizer can't record the candidate talking over the AI (dropped barge-in).
        handleCandidatePcmFrame(buf, signal);
      });
      let resp = await waitForStartup(apiRef.current.start());
      if (lifetime !== pageLifetimeRef.current) return;
      updateQuestionContext(resp);
      turnSeqRef.current = Math.max(turnSeqRef.current, resp.last_client_turn_id || 0);
      let restoredTurns = [];
      if (resuming) {
        try {
          const hist = await waitForStartup(apiRef.current.historyDetail());
          if (lifetime !== pageLifetimeRef.current) return;
          restoredTurns = (hist.turns || []).map((t) => ({ role: t.role, text: t.text, ts: t.ts }))
            .filter((t) => t.text && t.text.trim());
          setMessages(restoredTurns);
          if (hist.role_title) setRoleTitle(hist.role_title);
        } catch (error) {
          if (error?.code === 'interview_start_timeout' || error?.name === 'AbortError') throw error;
          // The start response still identifies the current pending question.
        }
      }
      let pendingFailure = null;
      if (!isMock) {
        try {
          const pendingUpload = await waitForStartup(VoiceUploadManager.restore(sessionId.trim(), candidateId.trim(), apiRef.current));
          if (lifetime !== pageLifetimeRef.current) return;
          if (pendingUpload) {
            uploadRef.current = pendingUpload;
            restoredUploadRef.current = pendingUpload;
            const recovery = pendingUpload.turn.recovery;
            if (recovery?.questionKey === questionKeyRef.current) clarificationCountRef.current = Number(recovery.count) || 0;
            turnSeqRef.current = Math.max(turnSeqRef.current, pendingUpload.turn.clientTurnId || 0);
            if (pendingUpload.turn.finalizing) {
              try {
                resp = await waitForStartup(pendingUpload.resume());
                uploadRef.current = null;
                turnSeqRef.current = Math.max(turnSeqRef.current, resp.last_client_turn_id || 0);
              } catch (error) {
                pendingFailure = error;
              }
            } else {
              warnAudio('Your previous recording was interrupted. Retry to deliver the saved audio, or type your answer.');
            }
          }
        } catch (error) {
          if (error?.code === 'interview_start_timeout' || error?.name === 'AbortError') throw error;
          warnAudio('Saved audio could not be checked. Retry or type your answer.');
        }
      }
      updateQuestionContext(resp);
      if (lifetime !== pageLifetimeRef.current) return;
      applyTurnState(resp);   // capture initial progress + inactivity config from the start response
      endedRef.current = false; busyRef.current = false;
      setCaption(''); captionRef.current = '';
      setClosingSeconds(null);
      if (pendingFailure) {
        phaseRef.current = 'call';
        setPhase('call');
        if (await handleVoiceFailure(pendingFailure)) return;
        settleVoiceOperation('checking');
        warnAudio('Your previous answer is saved. Checking delivery before recording another answer.');
        return;
      }

      if (preparationActive(resp?.preparation_status) || resp?.turn_type === 'preparing') {
        preparationGreetingHandledRef.current = false;
        setMessages([]); setElapsed(0);
        setAiState('thinking'); aiStateRef.current = 'thinking';
        setPhase('call');
        return;
      }
      if (String(resp?.preparation_status || '').toLowerCase() === 'failed') {
        preparationGreetingHandledRef.current = false;
        setAiState('idle'); aiStateRef.current = 'idle';
        setPhase('call');
        return;
      }
      preparationGreetingHandledRef.current = true;

      // ── Rejoin: restore the full prior conversation + current question, then resume listening
      //    (don't wipe history or replay past turns). Falls through to a fresh start if there's none.
      if (resuming) {
        try {
          if (restoredTurns.length) {
            // "Speaking" from the first call frame (set before setPhase, same reason as the fresh-start path).
            setAiState('speaking'); aiStateRef.current = 'speaking';
            setPhase('call');
            apiRef.current.presence(true, true).catch(() => {});  // noted as a rejoin
            nudgeCountRef.current = 0;
            const pending = (resp.interviewer_message || '').trim();   // the current pending question
            setCaption(''); captionRef.current = '';
            if (uploadRef.current) {
              setAiState('idle'); aiStateRef.current = 'idle';
              return;
            }
            if (pending) await speakAi(pending);   // re-voice the current question (no duplicate bubble)
            if (!endedRef.current) startListening();
            return;
          }
        } catch { /* no history → fall through to a normal start */ }
      }

      if (uploadRef.current) {
        setAiState('idle'); aiStateRef.current = 'idle';
        setPhase('call');
        return;
      }

      setMessages([]); setElapsed(0); setStage(''); setTopicProgress(null);
      setProgress({ asked: 0, target: null, total: null });
      // Show "Speaking" from the very first painted call frame. Set it BEFORE revealing the call UI —
      // otherwise the orb mounts on the initial 'idle' state ("Ready") until speakThenListen's own
      // setAiState('speaking') lands a frame later, which reads as a stuck "Ready" during the greeting.
      setAiState('speaking'); aiStateRef.current = 'speaking';
      setPhase('call');
      await speakThenListen(resp.interviewer_message);   // fresh start: greeting → then listen
    } catch (e) {
      if (lifetime !== pageLifetimeRef.current) return;
      const msg = (e && e.name === 'NotAllowedError')
        ? 'Microphone permission was denied. Allow the mic to join the interview.'
        : (e && e.message) || 'Could not start the interview (needs https or localhost).';
      showError(msg);
      if (embedded) setStartError(msg);     // show retry on the embedded loader (no preflight UI)
      else setPhase('preflight');           // standalone: back to the lobby; keep camera/screen
    } finally {
      clearTimeout(watchdog);
      if (joinAttemptRef.current === attempt) joinAttemptRef.current = null;
      if (lifetime === pageLifetimeRef.current) setJoining(false);
    }
  };

  // ── in-call media toggles ──
  const toggleCamera = async () => {
    // Camera ON → fully release the device so the hardware light turns off (not just hide frames).
    if (camera.stream || camera.starting) {
      if (RC.cameraStrict) { showError('Your camera must stay on for this interview.'); return; }
      camera.stop();
      return;
    }
    // Camera OFF → (re)acquire the device (prompts for permission if needed; the light comes back on).
    try { await camera.start(); }
    catch { showError('Camera is blocked. Allow camera access in your browser, then tap the camera again.'); }
  };
  const toggleShare = async () => {
    if (screen.sharing) {
      if (RC.screenStrict) { showError('Screen sharing must stay on for this interview.'); return; }
      screen.stop();
    } else {
      await requestShare();
    }
  };
  const toggleHideMirror = () => setHideMirror((v) => !v);

  // Embedded entry (from the onboarding experience): camera/mic/screen are already live, so skip
  // setup + preflight and start the interview straight away.
  // Cleanup invalidates pending startup; effect replay must start the replacement generation.
  const embeddedStartedRef = useRef(null);
  useEffect(() => {
    if (!embedded || embeddedStartedRef.current === pageLifetimeRef.current) return;
    if (!['setup', 'preflight'].includes(phaseRef.current)) return;
    if (!sessionId.trim() || !candidateId.trim()) return;
    embeddedStartedRef.current = pageLifetimeRef.current;
    onJoin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, sessionId, candidateId]);

  // ── finish → result ──
  const onFinished = useCallback(() => {
    if (endedRef.current) return;
    endedRef.current = true;
    windingRef.current = true;
    setWinding(true);
    setShareWarning('');
    stopListening();
    try { stopAll(); } catch { /* ignore */ }
    setAiState('idle');
    setClosingSeconds(closeDelayRef.current || 8);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopAll]);

  const loadResult = useCallback(async (interviewId) => {
    setEvaluating(true);
    try {
      const d = await apiRef.current.result(interviewId, false);
      setResultId(d.interview_id || interviewId || null);
      setTranscript(d.transcript_text || '');
      setMeta(d.metadata || {});
      setEvaluation(d.evaluation || null);
      // Job-seeker mock: the candidate-facing skill acknowledgement + cross-attempt history.
      if (isMock) {
        setAcknowledgement(d.acknowledgement || null);
        setSkillScores(d.skill_scores || null);
        try { const a = await apiRef.current.attempts(); setAttempts(a.attempts || []); } catch { /* ignore */ }
      }
    } catch {
      try {
        const t = await apiRef.current.transcript();
        setTranscript(t.transcript_text || ''); setMeta(t.metadata || {});
      } catch { /* ignore */ }
    } finally { setEvaluating(false); }
  }, [isMock]);

  useEffect(() => {
    if (closingSeconds == null) return undefined;
    if (closingSeconds <= 0) {
      setClosingSeconds(null);
      teardown();
      setPhase('done');
      if (!embedded || isMock) loadResult();   // mock seekers see their feedback on done
      return undefined;
    }
    const id = setTimeout(() => setClosingSeconds((s) => (s == null ? null : s - 1)), 1000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closingSeconds]);

  // ── controls ──
  const toggleMute = async () => {
    if (endedRef.current || windingRef.current) return;
    const next = !mutedRef.current && (micActivationRef.current
      || capture.getStream()?.getAudioTracks()[0]?.readyState === 'live');
    if (next) {
      applyMicMuted(true);
      pauseListeningForMute();
      if (!speakingAudioRef.current) setAiState('idle');
      if (!busyRef.current && !speakingAudioRef.current) armInactivity();
      return;
    }
    if (recoveryStateRef.current === 'checking'
        || (uploadRef.current && restoredUploadRef.current === uploadRef.current)) {
      warnAudio('Check delivery of your saved answer before starting another recording.');
      return;
    }
    applyMicMuted(false);
    textModeRef.current = false;
    setTextMode(false);
    if (micActivationRef.current) return;
    const lifetime = pageLifetimeRef.current;
    setMicStarting(true);
    const operation = (async () => {
      try {
        await startMic();
        if (lifetime !== pageLifetimeRef.current || endedRef.current || windingRef.current) return;
        setFrameSink((buf, signal) => handleCandidatePcmFrame(buf, signal));
        if (mutedRef.current || busyRef.current || speakingAudioRef.current) return;
        if (uploadRef.current && vadLastSpeechAtRef.current
            && !voiceSilenceRemaining(Date.now(), vadLastSpeechAtRef.current, lastCaptionActivityRef.current)) {
          await finalizeVoiceTurn('silence');
        } else {
          startListening({ resume: !!uploadRef.current });
        }
      } catch (error) {
        if (lifetime !== pageLifetimeRef.current || error?.name === 'AbortError') return;
        applyMicMuted(true);
        warnAudio('Microphone access failed. Check your browser permissions, then turn it on again.', 'device');
      } finally {
        if (lifetime === pageLifetimeRef.current) setMicStarting(false);
      }
    })();
    micActivationRef.current = operation;
    await operation;
    if (micActivationRef.current === operation) micActivationRef.current = null;
  };

  const handleEnd = () => {
    // Capture how much interview time is left so the modal can offer "keep going" (rejoin) when there's
    // still time, or end-only when the clock is nearly up (< 1 min → no point continuing).
    const total = progress.total || 0;
    setEndRemaining(total > 0 ? Math.max(0, total - (elapsed || 0)) : null);
    setConfirmEnd(true);
  };
  const confirmEndNow = async () => {
    setConfirmEnd(false);
    try { await apiRef.current.end('ended_by_candidate'); } catch { /* ignore */ }
    onFinished();
  };

  const handleRecalculate = async (basis = 'current_configuration') => {
    if (evaluating) return;
    setEvaluating(true);
    try {
      const d = await apiRef.current.result(resultId || undefined, true, basis);
      setEvaluation(d.evaluation || null);
      if (d.evaluation && d.evaluation.available) {
        showSuccess(`Recalculated: ${d.evaluation.overall_score == null ? 'Score unavailable' : `${d.evaluation.overall_score}/100`} · ${d.evaluation.decision_label}`);
      }
    } catch (e) { showError(e.message); } finally { setEvaluating(false); }
  };

  const handleViewHistory = async () => {
    if (!sessionId.trim() || !candidateId.trim()) {
      showError('Please enter both Session ID and Candidate ID to see past results.');
      return;
    }
    setHistoryLoading(true); setPhase('history');
    try { const d = await apiRef.current.history(); setHistory(d.interviews || []); }
    catch (e) { showError(e.message); } finally { setHistoryLoading(false); }
  };

  const handleOpenHistory = async (interviewId) => {
    endedRef.current = true;
    setMessages([]); setTranscript(''); setEvaluation(null);
    setPhase('done');
    await loadResult(interviewId);
  };

  const handleRestart = () => {
    teardown();
    endedRef.current = false; busyRef.current = false;
    setPhase('setup');
    setMessages([]); setCaption(''); setTranscript(''); setMeta({}); setEvaluation(null);
    setResultId(null); setHistory([]); setStage(''); setTopicProgress(null); setElapsed(null);
    setProgress({ asked: 0, target: null, total: null }); setClosingSeconds(null);
  };

  const toastEl = (
    <Toast
      message={toast.message}
      type={toast.type}
      visible={!!toast.message}
      onClose={() => setToast({ message: '', type: 'error' })}
    />
  );

  // embedded entry: streams already live, onJoin runs on mount — show a brief loader, not setup
  if (embedded && (phase === 'setup' || phase === 'preflight')) {
    return (
      <div className="flex h-[100dvh] w-full items-center justify-center bg-slate-100 text-slate-600 dark:bg-[#0a0a12] dark:text-slate-300">
        {startError ? (
          <div className="flex max-w-sm flex-col items-center gap-4 px-6 text-center">
            <p className="text-sm text-rose-600 dark:text-rose-400">{startError}</p>
            <button type="button" onClick={() => { setStartError(''); onJoin(); }}
              className="rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg hover:opacity-95">
              Try again
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <span className="h-8 w-8 animate-spin rounded-full border-2 border-primary-500 border-t-transparent dark:border-violet-500" />
            <p className="text-sm">{isRejoining ? 'Rejoining your interview…' : 'Starting your interview…'}</p>
          </div>
        )}
        {toastEl}
      </div>
    );
  }

  // ── full-screen meeting flows (gmeet-style; shown to the candidate) ──
  if (phase === 'preflight') {
    return (
      <>
        <PreflightGate
          camStream={camera.stream}
          cameraReady={!!camera.stream}
          cameraError={camera.error}
          onEnableCamera={onEnableCamera}
          sharing={screen.sharing}
          shareWarning={shareWarning}
          onShareScreen={requestShare}
          requireCamera={RC.cameraRequired}
          requireScreen={RC.screenRequired}
          requireMic={RC.micRequired}
          microphoneReady={capture.active}
          onEnableMic={() => startMic().catch(() => showError('Microphone access is required. Allow it in your browser settings and retry.'))}
          onAudioChecked={(passed) => {
            if (passed) clearAudioWarning();
            else warnAudio('We could not hear you clearly in the microphone test. Check your device or browser microphone settings, then retry. You can also type your answer.');
          }}
          onJoin={onJoin}
          joining={joining}
        />
        {toastEl}
      </>
    );
  }

  if (phase === 'call') {
    const subtitle = [
      roleTitle,
      stage || 'Introduction',
      `Q ${progress.asked || 0}${progress.target ? ` / ~${progress.target}` : ''}`,
    ].filter(Boolean).join(' · ');
    return (
      <>
        <MeetingRoom
          subtitle={subtitle}
          topicProgress={topicProgress}
          elapsed={elapsed}
          total={progress.total}
          connection="connected"
          audioWarning={audioWarningVisible || ['repeat', 'manual', 'checking'].includes(voiceRecovery) ? audioWarning : ''}
          audioWarningHidden={!!audioWarning && !audioWarningVisible && !['repeat', 'manual', 'checking'].includes(voiceRecovery)}
          audioWarningLabel={audioIssueKind === 'captions' ? 'Captions' : audioIssueKind === 'device' ? 'Microphone' : 'Answer delivery'}
          onShowAudioWarning={() => setAudioWarningVisible(true)}
          onRetryAudio={voiceRecovery === 'checking' ? retryAudio : recordAgain}
          onDoneSpeaking={() => finalizeVoiceTurn('manual_done')}
          canFinishSpeaking={aiState === 'listening' && !muted && !busyRef.current && !activeTask}
          onUseTyping={() => {
            if (recoveryStateRef.current === 'checking' || busyRef.current) {
              warnAudio('Please confirm delivery of the saved answer before sending a replacement.');
              return;
            }
            stopListening(); textModeRef.current = true; setTextMode(true);
            settleVoiceOperation('repeat');
          }}
          recoveryActionLabel={voiceRecovery === 'checking' ? 'Check delivery' : 'Record again'}
          recoveryBusy={voiceRecovery === 'processing' || voiceRecovery === 'clarifying'}
          recoveryCanType={voiceRecovery !== 'checking' && voiceRecovery !== 'clarifying'}
          aiState={aiState}
          stateLabel={voiceRecovery === 'checking' ? 'Checking answer delivery'
            : ['speaking', 'thinking'].includes(aiState) ? null
              : capture.error ? 'Microphone unavailable'
                : micStarting || (capture.active && capture.inputReady === false) ? 'Starting microphone'
                  : muted ? 'Microphone muted'
                    : listeningRef.current ? 'Listening' : textMode ? 'Typing' : activeTask ? 'Practical task' : 'Microphone on'}
          mutedSpeechVisible={mutedSpeechVisible && muted && !winding && !activeTask && !document.hidden
            && !['speaking', 'thinking'].includes(aiState) && voiceRecovery !== 'checking'}
          localSpeechMonitoring={muted && capture.active && closingSeconds == null}
          level={capture.level}
          messages={messages}
          inputActive={inputActive}
          camStream={camera.stream}
          cameraOn={!!camera.stream && camera.enabled}
          muted={muted}
          sharing={screen.sharing}
          screenStream={screen.stream}
          hideMirror={hideMirror}
          onToggleMute={toggleMute}
          onToggleCamera={toggleCamera}
          onToggleShare={toggleShare}
          onToggleHideMirror={toggleHideMirror}
          onEnd={handleEnd}
          typed={typed}
          setTyped={handleTypedChange}
          onSendTyped={(text) => sendTextTurn(text)}
          sendDisabled={aiState === 'thinking' || ['checking', 'clarifying'].includes(voiceRecovery)
            || closingSeconds != null || !!activeTask || preparationActive(preparation.status)}
          closingSeconds={closingSeconds}
          disabled={closingSeconds != null}
        />
        <PracticalTaskModal
          task={activeTask}
          answer={taskAnswer}
          onAnswer={handleTaskAnswerChange}
          onPaste={handleTaskPaste}
          onSubmit={() => submitPracticalTask('submitted', true)}
          submitting={taskSubmitting}
        />
        <ConfirmDialog
          open={taskConfirmOpen}
          title="Submit practical task?"
          message="After you submit, this task answer will be sent for evaluation and the interview will continue."
          confirmLabel="Submit task"
          cancelLabel="Keep editing"
          tone="primary"
          onConfirm={() => submitPracticalTask('submitted', false)}
          onCancel={() => setTaskConfirmOpen(false)}
        />
        <ConfirmDialog
          open={confirmEnd}
          title="End the interview?"
          message={endRemaining != null && endRemaining < rejoinMinRef.current
            ? 'Your time is almost up, so this will end the interview now. You won’t be able to continue once it ends.'
            : (endRemaining != null
                ? `You still have about ${Math.max(1, Math.ceil(endRemaining / 60))} min left. You can keep going, or end now — you won’t be able to continue once it ends.`
                : 'You won’t be able to continue this session once it ends.')}
          confirmLabel="End interview"
          cancelLabel="Keep going"
          // Below the configured min time left → no meaningful time to continue, so only offer End (no rejoin).
          hideCancel={endRemaining != null && endRemaining < rejoinMinRef.current}
          tone="danger"
          onConfirm={confirmEndNow}
          onCancel={() => setConfirmEnd(false)}
        />
        {/* Required-screen drop: a light, non-blocking banner (not a full-screen modal) that still forces
            a re-share — turns stay paused until the candidate shares their entire screen again. Hidden once
            the interview is winding up (stopping the share to leave must not nag "share again to continue"). */}
        {RC.screenRequired && !screen.sharing && !winding && closingSeconds == null && !endedRef.current && (
          <div className="fixed inset-x-0 bottom-4 z-50 flex justify-center px-4">
            <div className="flex w-full max-w-2xl items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 shadow-lg dark:border-amber-500/40 dark:bg-amber-950/90">
              <span className="text-xl" aria-hidden="true">🖥️</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">Screen sharing stopped</p>
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Share your <strong>entire screen</strong> again to continue — we’ll pick up right where you left off.
                  {shareWarning ? ` ${shareWarning}` : ''}
                </p>
              </div>
              <button type="button" onClick={onReshareDuringCall}
                className="shrink-0 rounded-lg bg-primary-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-primary-700">
                Share entire screen
              </button>
            </div>
          </div>
        )}
        {(preparationActive(preparation.status) || preparation.status === 'failed') && (
          <div className="fixed inset-0 z-[65] flex items-center justify-center bg-slate-950/45 px-4 backdrop-blur-sm">
            <div role="dialog" aria-modal="true" aria-labelledby="preparation-title" className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-700 dark:bg-slate-900">
              {preparation.status !== 'failed' ? (
                <>
                  <h2 id="preparation-title" className="text-base font-semibold text-slate-900 dark:text-white">Preparing your interview</h2>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-300" aria-live="polite">
                    Step {Math.max(1, PREPARATION_STEPS.findIndex((step) => step.stage === preparation.stage) + 1)} of {PREPARATION_STEPS.length}
                  </p>
                  <ol className="mt-5 space-y-3" aria-label="Preparation progress">
                    {PREPARATION_STEPS.map((step, index) => {
                      const { done, active } = preparationStepState(PREPARATION_STEPS, preparation, index);
                      const label = step.stage === 'candidate_intelligence' && preparation.hasResume === false
                        ? 'Reviewing available role information' : step.label;
                      return (
                        <li key={step.stage} aria-current={active ? 'step' : undefined}
                          className={`flex items-start gap-3 text-sm ${active ? 'font-semibold text-slate-900 dark:text-white' : done ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400 dark:text-slate-500'}`}>
                          <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full ${done ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200' : active ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-200' : 'bg-slate-100 dark:bg-slate-800'}`} aria-hidden="true">
                            {done ? <FiCheck size={14} /> : active ? <FiLoader size={14} className="animate-spin" /> : index + 1}
                          </span>
                          <span className="pt-0.5">{label}{active && step.stage === 'question_plan_generation' && preparation.questionProgress?.total > 0
                            ? ` (${preparation.questionProgress.completed} of ${preparation.questionProgress.total})` : ''}
                            {active && preparationSubstageLabel(preparation.substage) && (
                              <span className="mt-1 block text-xs font-normal text-slate-600 dark:text-slate-300">{preparationSubstageLabel(preparation.substage)}</span>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ol>
                  {preparation.stageUpdatedAt && preparationNow - new Date(preparation.stageUpdatedAt).getTime() > 45000 && (
                    <p className="mt-5 text-sm text-slate-600 dark:text-slate-300" role="status">
                      This is taking a little longer; your interview is still being prepared.
                    </p>
                  )}
                  {preparation.retryCount > 0 && (
                    <p className="mt-3 text-xs text-amber-700 dark:text-amber-300" role="status">
                      Retrying preparation
                    </p>
                  )}
                </>
              ) : (
                <>
                  <h2 id="preparation-title" className="text-base font-semibold text-rose-700 dark:text-rose-300">Interview preparation could not finish</h2>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                    {preparation.error || 'The assessment plan could not be prepared safely.'}
                  </p>
                  <button type="button" onClick={onJoin}
                    className="mt-4 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700">
                    Retry preparation
                  </button>
                </>
              )}
            </div>
          </div>
        )}
        {toastEl}
      </>
    );
  }

  // ── setup / history / done (centered, branded) ──
  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-[#F6F7F9] to-slate-200/60 dark:from-gray-950 dark:to-black flex flex-col items-center py-10 px-4">
      <div className="w-full max-w-4xl">
        <header className="mb-6 text-center">
          <span className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
            <span className="h-1.5 w-1.5 rounded-full bg-indigo-500 animate-pulse" /> Powered by Wiza
          </span>
          <h1 className="bg-gradient-to-r from-primary-600 via-accent-500 to-secondary-500 bg-clip-text text-3xl font-extrabold tracking-tight text-transparent">
            WIZBOX Interview Suite
          </h1>
          <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400">
            Meet <span className="font-semibold text-slate-700 dark:text-slate-200">Wiza</span>, your AI interviewer — speak naturally; she asks, listens, and moves on when you pause.
          </p>
        </header>

        {phase === 'setup' && (
          <AIInterviewSetup
            sessionId={sessionId}
            candidateId={candidateId}
            setSessionId={setSessionId}
            setCandidateId={setCandidateId}
            onStart={handleStart}
            onViewHistory={handleViewHistory}
            loading={joining}
          />
        )}

        {phase === 'history' && (
          <AIInterviewHistory
            interviews={history}
            loading={historyLoading}
            onOpen={handleOpenHistory}
            onBack={() => setPhase('setup')}
          />
        )}

        {phase === 'done' && (
          <AIInterviewCompletion
            candidateView={embedded}
            mock={isMock}
            acknowledgement={acknowledgement}
            skillScores={skillScores}
            attempts={attempts}
            transcript={transcript}
            meta={meta}
            evaluation={evaluation}
            evaluating={evaluating}
            onRecalculate={handleRecalculate}
            onRestart={handleRestart}
          />
        )}
      </div>

      {toastEl}
    </div>
  );
};

export default VoiceInterviewHybridPage;
